import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Paper,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Collapse,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckInIcon from '@mui/icons-material/Login';
import CheckOutIcon from '@mui/icons-material/Logout';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StoreIcon from '@mui/icons-material/Store';
import PeopleIcon from '@mui/icons-material/People';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import PublicIcon from '@mui/icons-material/Public';
import BabyChangingStationIcon from '@mui/icons-material/BabyChangingStation';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HotelIcon from '@mui/icons-material/Hotel';
import PaymentIcon from '@mui/icons-material/Payment';
import AddCardIcon from '@mui/icons-material/AddCard';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import RoomIcon from '@mui/icons-material/Room';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import { assignRooms, getCheckInAvailableRooms, processCheckIn, getBookingGuestDocuments, getRoomCleanings, getBookingDetails } from '../../services/api';
import api from '../../services/api';
import DashboardHousekeepingDrawer from '../dashboard/DashboardHousekeepingDrawer';
import { buildPendingCleaningByRoomId } from '../../utils/dashboardPendingTasks';
import { isRoomDirty, resolveWalkInHousekeepingCleaningId } from '../../utils/walkInHousekeeping';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { formatPaymentTime, toPaymentProcessedAtIso } from '../../utils/dateUtils';
import { resolveStayCheckInDisplay, resolveStayCheckOutDisplay, resolveActualCheckInDisplay, resolveActualCheckOutDisplay, isSameDayStay } from '../../utils/bookingStayDisplay';
import {
  buildRoomTypeSortOrderMap,
  resolveRoomTypeChangeKindFromItems,
  getRoomTypeChangeLabel,
  isGuestChild,
} from '../../utils/bookingUtils';
import ActualStayDateChips from './ActualStayDateChips';
import StayDurationChip from './StayDurationChip';
import moment from 'moment';
import { HotelDatePicker, HotelDateTimePicker as DateTimePicker } from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import countriesData from '../../data/countries-states-cities-data';
import ChannelImage from '../common/ChannelImage';
import PaymentLinkDrawerOverlay from './PaymentLinkDrawerOverlay';
import PaymentDrawerHeaderTitle from './PaymentDrawerHeaderTitle';
import {
  filterPaymentMethodsForComm,
  PAYMENT_LINK_METHOD,
  useBookingPaymentLinkComm,
} from '../../hooks/useBookingPaymentLinkComm';
import { PAYMENT_METHOD_CHIPS } from '../../utils/paymentMethodUtils';
import BookingAuditDrawer from './BookingAuditDrawer';
import {
  getBookingCurrencySymbol,
  getPaymentSummaryData,
  getEnrichedPaymentSummaryData,
} from '../../utils/bookingPaymentSummary';
import BookingPaymentSummaryCard from './BookingPaymentSummaryCard';
import BookingDrawerPaymentCard from './BookingDrawerPaymentCard';
import BookingFinancialHistoryPanel from './BookingFinancialHistoryPanel';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../../utils/locationSearch';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  getGuestDrawerContentSx,
  getGuestDrawerUploadPaperSx,
  getGuestDrawerMetaChipSx,
  bookingDrawerRoomImageSizeSx,
  createBookingDrawerOverlayProps,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import {
  getCheckInRoomHoldChipColor,
  getCheckInRoomHoldLabel,
  getCheckInRoomHoldOverlaySx,
  getCheckInRoomHoldReason,
  isStayWindowHoldReason,
  getCheckInRoomHoldTooltip,
} from '../../utils/checkInRoomHold';
import { GuestDocumentsDrawer, buildGuestDocumentsPayload } from './guestDocuments';
import {
  normalizeDocumentIdForSubmit,
} from '../../utils/documentIdFormatters';
import { BookingDrawerHeaderIdentity, DrawerGuestFieldLink } from './DrawerEntityLink';
import BookingGuestFormDrawer, { parseFullName } from './BookingGuestFormDrawer';
import {
  getForeignGuestChipSx,
  isForeignGuestRecord,
} from '../../utils/foreignGuestFormUtils';
import { getPermissionDeniedMessage, PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

const CUSTOM_BORDER_COLOR = '#4ce1b6';
const NO_DOCUMENT_VALUE = 'no_document';


const resolveHotelCountryName = () => {
  const hotel = (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel) || null;
  if (!hotel?.country) return '';
  const hotelCountryValue = String(hotel.country).trim();
  let countryObj = countriesData.countries.find(
    (c) => c.code === hotelCountryValue || c.name === hotelCountryValue,
  );
  if (!countryObj) {
    countryObj = countriesData.countries.find(
      (c) =>
        c.code?.toLowerCase() === hotelCountryValue.toLowerCase()
        || c.name?.toLowerCase() === hotelCountryValue.toLowerCase(),
    );
  }
  return countryObj?.name || hotelCountryValue;
};

const PURPOSE_OF_STAY_OPTIONS = [
  { value: 'business', label: 'Business' },
  { value: 'leisure', label: 'Leisure' },
  { value: 'medical', label: 'Medical' },
  { value: 'family_visit', label: 'Family Visit' },
  { value: 'event', label: 'Event' },
  { value: 'transit', label: 'Transit' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' },
];

const RESERVED_CONFIRM_TOOLTIP =
  'This booking is reserved and is not ready for check-in. Confirm it from Modify Booking, then continue with the check-in process.';

const CheckInDrawer = ({ bookingId, open, onClose, onCheckInSuccess, onModifyBooking, onBookingUpdated }) => {
  const { can } = usePermission();
  const canViewBookingAudit = can(PERMISSIONS.BOOKING_AUDIT_VIEW);
  const canEditBooking = can(PERMISSIONS.BOOKING_MODIFY);
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const hotelCountry = useMemo(() => resolveHotelCountryName(), []);
  const drawerTheme = getBookingDrawerTheme(isDark);
  const { selectMenuProps, datePickerSlotProps } = useMemo(
    () => createBookingDrawerOverlayProps(isDark),
    [isDark],
  );

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  // Removed loading state - drawer opens immediately, content shows as data arrives
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [cameraPermissionHint, setCameraPermissionHint] = useState({ open: false, message: '' });
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [availableRooms, setAvailableRooms] = useState(null);
  const [booking, setBooking] = useState(null);
  const [bookingItems, setBookingItems] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState({}); // {booking_item_id: room_id}
  const [selectedRoomIds, setSelectedRoomIds] = useState({}); // {room_type_id: Set(room_ids)} - for quick multi-select
  const [purposeOfStay, setPurposeOfStay] = useState('');
  const [purposeOfStayNote, setPurposeOfStayNote] = useState('');
  const [savingGuests, setSavingGuests] = useState(false);
  const [individualGuestDrawerOpen, setIndividualGuestDrawerOpen] = useState(false);
  const [editingGuestItemId, setEditingGuestItemId] = useState(null);
  const [editingGuestId, setEditingGuestId] = useState(null); // Guest ID when editing existing guest (update in place, don't create new)
  const [editingRoomId, setEditingRoomId] = useState(null); // Track which room is being edited for guest addition
  // Per-room guests staged in UI until check-in (not written to DB until process check-in).
  const [pendingRoomGuests, setPendingRoomGuests] = useState({}); // { [roomId]: guestFormData }
  const [deferGuestPersist, setDeferGuestPersist] = useState(false);
  // initialGuestFormData: prefill snapshot passed to BookingGuestFormDrawer when opening
  const [initialGuestFormData, setInitialGuestFormData] = useState(null);
  const [guestSlideIndex, setGuestSlideIndex] = useState({}); // {roomId: slideIndex} for guest slider per room
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);
  const [selectedGuestDocuments, setSelectedGuestDocuments] = useState(null); // {id_front_url, id_back_url, document_type, guestName, guestPhone, bookingCode}
  const [deleteGuestDialogOpen, setDeleteGuestDialogOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null); // {guest, bookingItemId, roomId, guestName}
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [paymentCameraOpen, setPaymentCameraOpen] = useState(false);
  const [paymentStream, setPaymentStream] = useState(null);
  const [paymentCameraUpload, setPaymentCameraUpload] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(() => moment());
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentLinkSendOpen, setPaymentLinkSendOpen] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const paymentReceiptVideoRef = useRef(null);
  const paymentReceiptCanvasRef = useRef(null);
  const paymentReceiptFileInputRef = useRef(null);
  const paymentDrawerRootRef = useRef(null);
  const [housekeepingCleaningId, setHousekeepingCleaningId] = useState(null);
  const [housekeepingResolving, setHousekeepingResolving] = useState(false);
  const [housekeepingError, setHousekeepingError] = useState('');
  const [pendingCleaningByRoomId, setPendingCleaningByRoomId] = useState({});
  const [dirtyRoomsPendingClean, setDirtyRoomsPendingClean] = useState(() => new Set());
  const dirtyRoomQueueRef = useRef([]);
  const housekeepingRoomRef = useRef(null);
  const housekeepingDrawerOpenRef = useRef(false);
  const editingRoomIdRef = useRef(null);
  
  // Payment methods matching walk-in review
  const {
    canSendPaymentLink,
    canSendPaymentLinkEmail,
    canSendPaymentLinkWhatsApp,
  } = useBookingPaymentLinkComm();
  const paymentMethods = useMemo(
    () => filterPaymentMethodsForComm(PAYMENT_METHOD_CHIPS, canSendPaymentLink),
    [canSendPaymentLink]
  );
  useEffect(() => {
    if (!canSendPaymentLink && paymentMethod === PAYMENT_LINK_METHOD) {
      setPaymentMethod('');
      setPaymentLinkSendOpen(false);
    }
  }, [canSendPaymentLink, paymentMethod]);
  const drawerContainerRef = useRef(null);
  const theme = useTheme();
  const auditBookingId = useMemo(() => {
    const parsedId = Number(booking?.id || booking?.booking_id || bookingId);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  }, [booking?.id, booking?.booking_id, bookingId]);

  // Fetch available rooms when drawer opens
  useEffect(() => {
    if (open && bookingId) {
      fetchAvailableRooms();
    } else {
      // Reset state when drawer closes
      setAvailableRooms(null);
      setBooking(null);
      setSelectedRooms({});
      setSelectedRoomIds({});
      setPendingRoomGuests({});
      setDeferGuestPersist(false);
      setPurposeOfStay('');
      setPurposeOfStayNote('');
      setError(null);
      setSuccessMessage(null);
      setAuditDrawerOpen(false);
      setIndividualGuestDrawerOpen(false);
      setEditingGuestItemId(null);
      setEditingGuestId(null);
      setPaymentLinkSendOpen(false);
      setPaymentDrawerOpen(false);
      setHousekeepingCleaningId(null);
      setHousekeepingResolving(false);
      setHousekeepingError('');
      setPendingCleaningByRoomId({});
      setDirtyRoomsPendingClean(new Set());
      dirtyRoomQueueRef.current = [];
      housekeepingRoomRef.current = null;
      housekeepingDrawerOpenRef.current = false;
    }
  }, [open, bookingId]);

  const fetchAvailableRooms = async ({ preserveSelection = false } = {}) => {
    setError(null);
    try {
      // Only fetch check-in data - it already contains all booking information
      // Removed unnecessary getBookingDetails call to improve performance
      const data = await getCheckInAvailableRooms(bookingId);

      setAvailableRooms(data.room_types || []);
      const bookingData = data.booking;
      setBookingItems(data.booking_items || []);
      const heldRoomIds = new Set();
      const currentlyAllocatedIds = new Set(
        (data.booking_items || []).flatMap((item) => {
          const checkoutRaw = item?.checkout;
          const isCheckedOut = typeof checkoutRaw === 'string'
            ? ['1', 'true', 'yes'].includes(checkoutRaw.trim().toLowerCase())
            : checkoutRaw === true || checkoutRaw === 1;
          if (isCheckedOut) return [];
          const allocatedId = item?.allocated_room_id ?? item?.allocated_room?.id ?? null;
          return allocatedId != null && allocatedId !== '' ? [Number(allocatedId)] : [];
        }),
      );
      (data.room_types || []).forEach((group) => {
        (group.rooms || []).forEach((room) => {
          const reason = getCheckInRoomHoldReason(room);
          if (!reason) return;
          if (currentlyAllocatedIds.has(Number(room.id)) && isStayWindowHoldReason(reason)) return;
          heldRoomIds.add(Number(room.id));
        });
      });
      const isItemCheckedIn = (item) => {
        const checkedInRaw = item?.checked_in;
        return typeof checkedInRaw === 'string'
          ? ['1', 'true', 'yes'].includes(checkedInRaw.trim().toLowerCase())
          : checkedInRaw === true || checkedInRaw === 1;
      };
      const keepHeldRoom = (roomId) => (
        (data.booking_items || []).some((item) => {
          if (!isItemCheckedIn(item)) return false;
          const allocatedId = item?.allocated_room_id
            ?? item?.allocated_room?.id
            ?? null;
          return allocatedId != null && Number(allocatedId) === Number(roomId);
        })
      );
      const stripHeldSelection = (roomIdsByType) => {
        const next = {};
        Object.entries(roomIdsByType || {}).forEach(([roomTypeId, roomIds]) => {
          next[roomTypeId] = (roomIds || []).map((roomId) => {
            if (roomId == null) return roomId;
            if (!heldRoomIds.has(Number(roomId))) return roomId;
            return keepHeldRoom(roomId) ? roomId : null;
          });
        });
        return next;
      };

      // Batch state updates to reduce re-renders
      setBooking(bookingData);

      const bookingStatusLower = String(bookingData?.status || '').toLowerCase();
      if (bookingStatusLower === 'confirmed') {
        setPurposeOfStay(bookingData.purpose_of_stay ? String(bookingData.purpose_of_stay) : '');
        setPurposeOfStayNote(
          bookingData.purpose_of_stay_note ? String(bookingData.purpose_of_stay_note) : ''
        );
      } else if (!preserveSelection) {
        setPurposeOfStay('');
        setPurposeOfStayNote('');
      }

      // Calculate payment info
      const remainingAmount = bookingData?.remaining_amount !== undefined && bookingData?.remaining_amount !== null
        ? bookingData.remaining_amount
        : ((bookingData?.total_amount || bookingData?.total || 0) - (bookingData?.advance_amount || bookingData?.advance || bookingData?.advance_paid || 0));

      // Pre-select allocated rooms on initial load only.
      // After Add Guest / refresh, keep the user's room picks (e.g. 206 replacing 205).
      if (!preserveSelection) {
        const skipPreselectAllocated =
          bookingStatusLower === 'partial_checkout' || bookingStatusLower === 'partial checkout';
        const preselectOnlyCheckedInRooms = [
          'partial_checkin',
          'partial check-in',
          'checked_in',
        ].includes(bookingStatusLower);
        const preSelected = {};
        const preSelectedRoomIds = {};
        if (!skipPreselectAllocated) {
          const activeItems = (data.booking_items || []).filter((item) => {
            const checkoutRaw = item?.checkout;
            const isItemCheckedOut = typeof checkoutRaw === 'string'
              ? ['1', 'true', 'yes'].includes(checkoutRaw.trim().toLowerCase())
              : checkoutRaw === true || checkoutRaw === 1;
            return !isItemCheckedOut;
          });
          const roomTypeIds = [...new Set(activeItems.map((item) => String(item.room_type_id)))];
          roomTypeIds.forEach((roomTypeId) => {
            const itemsForType = activeItems.filter((item) => String(item.room_type_id) === roomTypeId);
            const allocationGroups = groupItemsBySequence(itemsForType);
            preSelectedRoomIds[roomTypeId] = [];
            Object.values(allocationGroups).forEach((itemsForGroup) => {
              const allocatedRoomId = (itemsForGroup || []).find((item) => {
                if (!item?.allocated_room_id) return false;
                if (!preselectOnlyCheckedInRooms) return true;
                const checkedInRaw = item?.checked_in;
                return typeof checkedInRaw === 'string'
                  ? ['1', 'true', 'yes'].includes(checkedInRaw.trim().toLowerCase())
                  : checkedInRaw === true || checkedInRaw === 1;
              })?.allocated_room_id || null;
              preSelectedRoomIds[roomTypeId].push(allocatedRoomId);
              (itemsForGroup || []).forEach((item) => {
                preSelected[item.id] = allocatedRoomId;
              });
            });
          });
        }

        const nextRoomIds = stripHeldSelection(preSelectedRoomIds);
        Object.keys(preSelected).forEach((itemId) => {
          const roomId = preSelected[itemId];
          if (roomId != null && heldRoomIds.has(Number(roomId)) && !keepHeldRoom(roomId)) {
            preSelected[itemId] = null;
          }
        });
        setSelectedRooms(preSelected);
        setSelectedRoomIds(nextRoomIds);
      } else {
        setSelectedRoomIds((prev) => stripHeldSelection(prev));
        setSelectedRooms((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((itemId) => {
            const roomId = next[itemId];
            if (roomId != null && heldRoomIds.has(Number(roomId)) && !keepHeldRoom(roomId)) {
              next[itemId] = null;
            }
          });
          return next;
        });
      }
      const round2 = (x) => Math.round(Number(x) * 100) / 100;
      setPaymentAmount(round2(remainingAmount));
      // Don't fetch payment history here - lazy load when payment drawer opens
      // This improves initial drawer opening performance
    } catch (err) {
      setError('Failed to load available rooms. Please try again.');
    }
  };

  const fetchPaymentHistory = async (bookingCode) => {
    if (!bookingCode) return;
    setLoadingPayments(true);
    try {
      const response = await api.get('/finance/api/payments/', {
        params: { booking_code: bookingCode }
      });
      const data = response.data;
      let payments = [];
      if (data) {
        if (Array.isArray(data)) {
          payments = data;
        } else if (data.Data && Array.isArray(data.Data)) {
          payments = data.Data;
        } else if (data.data && Array.isArray(data.data)) {
          payments = data.data;
        } else if (data.results && Array.isArray(data.results)) {
          payments = data.results;
        } else if (data.payments && Array.isArray(data.payments)) {
          payments = data.payments;
        }
      }
      setPaymentHistory(payments);
    } catch (err) {
      setPaymentHistory([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const addDirtyRoomPending = useCallback((roomId) => {
    setDirtyRoomsPendingClean((prev) => {
      const next = new Set(prev);
      next.add(Number(roomId));
      return next;
    });
  }, []);

  const removeDirtyRoomPending = useCallback((roomId) => {
    setDirtyRoomsPendingClean((prev) => {
      const next = new Set(prev);
      next.delete(Number(roomId));
      return next;
    });
  }, []);

  const unselectRoomById = useCallback((roomId, roomTypeId) => {
    setSelectedRoomIds((prev) => {
      const current = prev[roomTypeId] || [];
      if (!current.some((id) => Number(id) === Number(roomId))) return prev;
      return {
        ...prev,
        [roomTypeId]: current.filter((id) => Number(id) !== Number(roomId)),
      };
    });
    removeDirtyRoomPending(roomId);
  }, [removeDirtyRoomPending]);

  const markRoomCleanInState = useCallback((roomId) => {
    setAvailableRooms((prev) => {
      if (!prev) return prev;
      return prev.map((group) => ({
        ...group,
        rooms: (group.rooms || []).map((room) => (
          Number(room.id) === Number(roomId) ? { ...room, clean_status: 'clean' } : room
        )),
      }));
    });
    removeDirtyRoomPending(roomId);
  }, [removeDirtyRoomPending]);

  const loadPendingCleanings = useCallback(async () => {
    try {
      const response = await getRoomCleanings({ status: 'PENDING', page_size: 500 });
      setPendingCleaningByRoomId(buildPendingCleaningByRoomId(response));
    } catch {
      setPendingCleaningByRoomId({});
    }
  }, []);

  useEffect(() => {
    if (open && bookingId) {
      loadPendingCleanings();
    }
  }, [open, bookingId, loadPendingCleanings]);

  const openDirtyRoomCleaning = useCallback(async (roomId, roomTypeId) => {
    if (housekeepingResolving || housekeepingDrawerOpenRef.current) {
      const key = String(roomId);
      if (!dirtyRoomQueueRef.current.some((entry) => String(entry.roomId) === key)) {
        dirtyRoomQueueRef.current.push({ roomId, roomTypeId });
      }
      return;
    }
    setHousekeepingResolving(true);
    setHousekeepingError('');
    try {
      const cleaningId = await resolveWalkInHousekeepingCleaningId(roomId, pendingCleaningByRoomId);
      housekeepingRoomRef.current = { roomId, roomTypeId };
      housekeepingDrawerOpenRef.current = true;
      setHousekeepingCleaningId(cleaningId);
      await loadPendingCleanings();
    } catch (err) {
      const message = err?.error || err?.detail || err?.message || 'Could not open housekeeping for this room.';
      setHousekeepingError(message);
      unselectRoomById(roomId, roomTypeId);
      dirtyRoomQueueRef.current = dirtyRoomQueueRef.current.filter(
        (entry) => Number(entry.roomId) !== Number(roomId),
      );
    } finally {
      setHousekeepingResolving(false);
    }
  }, [housekeepingResolving, pendingCleaningByRoomId, loadPendingCleanings, unselectRoomById]);

  const processNextDirtyRoomInQueue = useCallback(() => {
    if (housekeepingDrawerOpenRef.current || housekeepingResolving) return;
    const next = dirtyRoomQueueRef.current.shift();
    if (!next) return;
    openDirtyRoomCleaning(next.roomId, next.roomTypeId);
  }, [housekeepingResolving, openDirtyRoomCleaning]);

  const enqueueDirtyRoomCleaning = useCallback((roomId, roomTypeId) => {
    addDirtyRoomPending(roomId);
    if (housekeepingDrawerOpenRef.current) {
      const key = String(roomId);
      if (!dirtyRoomQueueRef.current.some((entry) => String(entry.roomId) === key)) {
        dirtyRoomQueueRef.current.push({ roomId, roomTypeId });
      }
      return;
    }
    openDirtyRoomCleaning(roomId, roomTypeId);
  }, [addDirtyRoomPending, openDirtyRoomCleaning]);

  const handleHousekeepingClose = useCallback(() => {
    const ctx = housekeepingRoomRef.current;
    if (ctx) {
      // Keep auto-allocated rooms selected; only drop manually picked dirty rooms.
      const wasAutoAllocated = bookingItems.some((item) => {
        const allocatedId = item.allocated_room_id
          || (item.allocated_room && (item.allocated_room.id || item.allocated_room.room_id))
          || null;
        return allocatedId != null && Number(allocatedId) === Number(ctx.roomId);
      });
      if (!wasAutoAllocated) {
        unselectRoomById(ctx.roomId, ctx.roomTypeId);
      }
      dirtyRoomQueueRef.current = dirtyRoomQueueRef.current.filter(
        (entry) => Number(entry.roomId) !== Number(ctx.roomId),
      );
    }
    housekeepingRoomRef.current = null;
    housekeepingDrawerOpenRef.current = false;
    setHousekeepingCleaningId(null);
    window.setTimeout(() => processNextDirtyRoomInQueue(), 0);
  }, [bookingItems, unselectRoomById, processNextDirtyRoomInQueue]);

  const handleHousekeepingSuccess = useCallback(async ({ roomId } = {}) => {
    const cleanedRoomId = roomId ?? housekeepingRoomRef.current?.roomId;
    housekeepingRoomRef.current = null;
    housekeepingDrawerOpenRef.current = false;
    setHousekeepingCleaningId(null);
    if (cleanedRoomId != null) {
      setPendingCleaningByRoomId((prev) => {
        const next = { ...prev };
        delete next[String(cleanedRoomId)];
        return next;
      });
      markRoomCleanInState(cleanedRoomId);
    }
    await loadPendingCleanings();
    window.setTimeout(() => processNextDirtyRoomInQueue(), 0);
  }, [markRoomCleanInState, loadPendingCleanings, processNextDirtyRoomInQueue]);

  const handleHousekeepingError = useCallback((message) => {
    setHousekeepingError(message || 'Failed to mark room as clean.');
    loadPendingCleanings();
  }, [loadPendingCleanings]);

  const showCameraPermissionHint = useCallback((message) => {
    setCameraPermissionHint({
      open: true,
      message:
        message ||
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings, or turn off Camera upload to choose a file for the receipt.',
    });
  }, []);

  const startPaymentCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraPermissionHint(
        'Camera is not available. Turn off Camera upload to upload a receipt image from files.'
      );
      return;
    }
    try {
      if (paymentStream) {
        paymentStream.getTracks().forEach(track => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setPaymentStream(mediaStream);
      setPaymentCameraOpen(true);
      setTimeout(() => {
        if (paymentReceiptVideoRef.current) {
          paymentReceiptVideoRef.current.srcObject = mediaStream;
          paymentReceiptVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (error) {
      showCameraPermissionHint();
    }
  };

  const stopPaymentCamera = () => {
    if (paymentStream) {
      paymentStream.getTracks().forEach(track => track.stop());
      setPaymentStream(null);
    }
    setPaymentCameraOpen(false);
  };

  const capturePaymentReceipt = () => {
    if (paymentReceiptVideoRef.current && paymentReceiptCanvasRef.current) {
      const video = paymentReceiptVideoRef.current;
      const canvas = paymentReceiptCanvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setPaymentReceipt(imageData);
      stopPaymentCamera();
    }
  };

  const handlePaymentReceiptUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPaymentReceipt(event.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please upload an image file.');
      }
    }
  };

  const handlePaymentReceiptClick = () => {
    if (paymentCameraUpload) {
      startPaymentCamera();
    } else {
      paymentReceiptFileInputRef.current?.click();
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) {
      setPaymentError('Please enter a valid payment amount.');
      return;
    }
    if (!paymentMethod) {
      setPaymentError('Please select a payment method.');
      return;
    }
    if (paymentMethod === 'PAYMENT LINK') {
      setPaymentError('Use Email, WhatsApp, or Both in the payment link panel.');
      return;
    }

    const processedAtIso = toPaymentProcessedAtIso(paymentDate);

    setSubmittingPayment(true);
    setPaymentError(null);
    try {
      let response;
      if (paymentReceipt) {
        const formData = new FormData();
        formData.append('booking', bookingId);
        formData.append('amount', paymentAmount);
        formData.append('payment_type', 'ADVANCE');
        formData.append('payment_method', paymentMethod);
        if (referenceNumber) {
          formData.append('reference_number', referenceNumber);
        }
        formData.append('status', 'SUCCESS');
        if (processedAtIso) formData.append('processed_at', processedAtIso);
        
        if (paymentReceipt.startsWith('data:image')) {
          const blobResponse = await fetch(paymentReceipt);
          const blob = await blobResponse.blob();
          formData.append('document', blob, 'payment_receipt.jpg');
        } else if (paymentReceipt instanceof File) {
          formData.append('document', paymentReceipt);
        }

        response = await api.post('/finance/api/payments/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        response = await api.post('/finance/api/payments/', {
          booking: bookingId,
          amount: paymentAmount,
          payment_type: 'ADVANCE',
          payment_method: paymentMethod,
          reference_number: referenceNumber || null,
          status: 'SUCCESS',
          processed_at: processedAtIso,
        });
      }

      if (response.data) {
        setPaymentAmount(0);
        setPaymentDate(moment());
        setPaymentMethod('');
        setReferenceNumber('');
        setPaymentReceipt(null);
        setPaymentCameraUpload(false);
        setPaymentError(null);
        setPaymentDrawerOpen(false);
        setSubmittingPayment(false);
        // Patch booking card immediately; refresh drawer data in background.
        onBookingUpdated?.(bookingId);
        void (async () => {
          try {
            await Promise.all([
              fetchPaymentHistory(booking?.code || booking?.booking_code),
              fetchAvailableRooms(),
            ]);
          } catch {
            // Keep drawer usable; card already patched.
          }
        })();
      }
    } catch (err) {
      setPaymentError(err.response?.data?.error || err.message || 'Failed to process payment.');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const getBookingItemDateKey = useCallback((item) => {
    if (!item?.date) return '';
    return String(item.date).slice(0, 10);
  }, []);

  // Same slot model as RoomAllocationDrawer:
  // normal case groups by sequence; duplicate same-date sequence rows split per item.
  const groupItemsBySequence = useCallback((items) => {
    const bySequenceDate = new Map();
    items.forEach((item) => {
      const seq = Number(item?.room_sequence || 1);
      const dateKey = getBookingItemDateKey(item);
      const key = `${seq}_${dateKey}`;
      bySequenceDate.set(key, (bySequenceDate.get(key) || 0) + 1);
    });

    const itemsBySequence = {};
    items.forEach((item) => {
      const seq = Number(item?.room_sequence || 1);
      const dateKey = getBookingItemDateKey(item);
      const duplicateSameDateSeq = (bySequenceDate.get(`${seq}_${dateKey}`) || 0) > 1;
      const groupKey = duplicateSameDateSeq ? `item_${item?.id}` : `seq_${seq}`;
      if (!itemsBySequence[groupKey]) {
        itemsBySequence[groupKey] = [];
      }
      itemsBySequence[groupKey].push(item);
    });
    return itemsBySequence;
  }, [getBookingItemDateKey]);

  // Auto-update payment amount when booking's remaining amount changes
  useEffect(() => {
    if (booking) {
      // Use payment_summary if available, otherwise calculate from booking fields
      const paymentSummary = booking.payment_summary;
      const remainingAmount = paymentSummary?.remaining_balance !== undefined
        ? paymentSummary.remaining_balance
        : (booking.remaining_amount !== undefined && booking.remaining_amount !== null
        ? booking.remaining_amount
          : ((booking.total_amount || booking.total || 0) - (booking.advance_amount || booking.advance || booking.advance_paid || 0)));
      
      const round2 = (x) => Math.round(Number(x) * 100) / 100;
      if (remainingAmount !== undefined && remainingAmount !== null) {
        setPaymentAmount(round2(remainingAmount));
        // Don't automatically enable payment collection - let user choose
        // Disable if there's no remaining amount or if booking is confirmed
      }
    }
  }, [booking?.payment_summary, booking?.remaining_amount, booking?.total_amount, booking?.advance_amount, booking]);

  // Lazy load payment history only when payment drawer opens
  useEffect(() => {
    if (paymentDrawerOpen && booking?.code) {
      // Only fetch payment history when payment drawer is opened
      // This improves initial drawer opening performance
      fetchPaymentHistory(booking.code || booking.booking_code);
    }
  }, [paymentDrawerOpen, booking?.code, booking?.booking_code]);

  // Sync selectedRoomIds with selectedRooms - assign rooms to booking items by room_type and sequence
  // Items with the same (room_type, sequence) get the same room across all dates
  // This ensures consistent room assignment: same room type + sequence = same room
  // Each (room_type, sequence) combination must get a UNIQUE room
  // Follows the same logic as RoomAllocationDrawer
  useEffect(() => {
    if (!bookingItems.length || !Object.keys(selectedRoomIds).length) {
      return;
    }
    
    const newSelectedRooms = {};
    const bookingStatusLower = String(booking?.status || '').toLowerCase();
    const isFlagTrue = (raw) => {
      if (typeof raw === 'string') {
        return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
      }
      return raw === true || raw === 1;
    };
    const hasPendingCheckInStreams = (() => {
      const streamFullyCheckedIn = new Map();
      bookingItems.forEach((item) => {
        if (item?.room_type_id == null || isFlagTrue(item?.checkout)) return;
        const streamKey = `${item.room_type_id}_${item.room_sequence || 1}_${item.allocated_room_id || item.id}`;
        const itemCheckedIn = isFlagTrue(item?.checked_in);
        const previous = streamFullyCheckedIn.get(streamKey);
        streamFullyCheckedIn.set(
          streamKey,
          previous === undefined ? itemCheckedIn : previous && itemCheckedIn
        );
      });
      return Array.from(streamFullyCheckedIn.values()).some((fullyCheckedIn) => !fullyCheckedIn);
    })();
    const isContinueCheckInStatus = [
      'partial_checkin',
      'partial check-in',
      'partial_checkout',
      'partial checkout',
    ].includes(bookingStatusLower)
      || (bookingStatusLower === 'checked_in' && hasPendingCheckInStreams);
    
    Object.keys(selectedRoomIds).forEach((roomTypeId) => {
      const rawSelectedIds = selectedRoomIds[roomTypeId] || [];
      const lockedCheckedInRoomIds = new Set();
      if (isContinueCheckInStatus) {
        bookingItems.forEach((item) => {
          if (Number(item.room_type_id) !== Number(roomTypeId)) return;
          if (isFlagTrue(item?.checkout) || !isFlagTrue(item?.checked_in)) return;
          const allocatedRoomId = item?.allocated_room_id
            ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
            ?? null;
          if (allocatedRoomId != null && allocatedRoomId !== '') {
            lockedCheckedInRoomIds.add(Number(allocatedRoomId));
          }
        });
      }
      const selectedIds = rawSelectedIds.filter((roomId) =>
        roomId != null && !lockedCheckedInRoomIds.has(Number(roomId))
      );
      if (selectedIds.length === 0) return;
      
      const itemsForThisType = bookingItems.filter((item) => {
        if (item.room_type_id !== parseInt(roomTypeId)) return false;
        if (!isContinueCheckInStatus) return true;
        if (isFlagTrue(item?.checkout)) return false;
        if (isFlagTrue(item?.checked_in)) return false;
        return true;
      });
      if (itemsForThisType.length === 0) return;
      
      // Group items by sequence - items with same sequence should get same room
      // Don't sort by date first - just group by sequence
      const itemsBySequence = groupItemsBySequence(itemsForThisType);
      const sequences = Object.keys(itemsBySequence).sort((a, b) => String(a).localeCompare(String(b)));
      
      // Track which rooms have been assigned to ensure uniqueness
      const assignedRooms = new Set();
      
      // Assign rooms to each sequence in order
      // Sequence 1 gets the first selected room, sequence 2 gets the second, etc.
      // Each (room_type, sequence) combination must get a UNIQUE room
      sequences.forEach((sequence) => {
        const itemsForSequence = itemsBySequence[sequence] || [];
        const currentAllocatedRoomId = itemsForSequence
          .map((item) => item?.allocated_room_id
            ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
            ?? null)
          .find((roomId) => roomId != null && roomId !== '');

        // Find the next available room that hasn't been assigned to another sequence
        // This ensures each (room_type, sequence) gets a unique room
        let roomId = null;
        const preferredRoomId = selectedIds.find(
          (selectedId) => Number(selectedId) === Number(currentAllocatedRoomId)
        );
        if (preferredRoomId != null && !assignedRooms.has(Number(preferredRoomId))) {
          roomId = preferredRoomId;
          assignedRooms.add(Number(preferredRoomId));
        }
        for (let i = 0; !roomId && i < selectedIds.length; i++) {
          const candidateRoomId = selectedIds[i];
          const normalizedCandidateRoomId = Number(candidateRoomId);
          if (!assignedRooms.has(normalizedCandidateRoomId)) {
            roomId = candidateRoomId;
            assignedRooms.add(normalizedCandidateRoomId);
            break;
          }
        }
        
        // If no unique room available, skip this sequence (don't assign duplicate rooms)
        if (!roomId) {
          return;
        }
        
        // Assign this room to ALL items with this sequence (across all dates)
        // This ensures items with the same (room_type, sequence) get the same room
        itemsForSequence.forEach((item) => {
          newSelectedRooms[item.id] = roomId;
        });
      });
    });
    setSelectedRooms(newSelectedRooms);
  }, [selectedRoomIds, bookingItems, groupItemsBySequence, booking?.status]);
  
  // Handle selecting/deselecting a room - allows multiple selections - memoized
  const handleRoomToggle = useCallback((roomId, roomTypeId) => {
    setSelectedRoomIds((prev) => {
      const currentSelected = prev[roomTypeId] || [];
      const isRoomSelected = currentSelected.some((id) => Number(id) === Number(roomId));
      
      const newState = { ...prev };
      if (!newState[roomTypeId]) {
        newState[roomTypeId] = [];
      }
      
      if (isRoomSelected) {
        // Deselect: remove from array (checked-in rooms are locked in the UI)
        newState[roomTypeId] = newState[roomTypeId].filter(
          (id) => Number(id) !== Number(roomId)
        );
        setPendingRoomGuests((prev) => {
          if (prev[roomId] == null && prev[String(roomId)] == null) return prev;
          const next = { ...prev };
          delete next[roomId];
          delete next[String(roomId)];
          return next;
        });
      } else {
        const bookingStatus = String(booking?.status || '').toLowerCase();
        const isPartialCheckInStatus =
          bookingStatus === 'partial_checkin' || bookingStatus === 'partial check-in';
        const isPartialCheckoutStatus =
          bookingStatus === 'partial_checkout' || bookingStatus === 'partial checkout';
        const isCheckedInStatus = bookingStatus === 'checked_in';
        const isItemFlagTrue = (raw) => {
          if (typeof raw === 'string') {
            return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
          }
          return raw === true || raw === 1;
        };
        const itemsForType = (bookingItems || []).filter(
          (item) => Number(item.room_type_id) === Number(roomTypeId),
        );
        // Count unique room streams (more reliable than items/nights).
        const roomsNeededForType = Object.keys(
          groupItemsBySequence(itemsForType.filter((item) => !isItemFlagTrue(item?.checkout)))
        ).length || itemsForType.length;
        const checkedInIds = new Set();
        itemsForType.forEach((item) => {
          if (isItemFlagTrue(item?.checkout) || !isItemFlagTrue(item?.checked_in)) return;
          const allocatedId = item?.allocated_room_id
            ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
            ?? null;
          if (allocatedId != null && allocatedId !== '') checkedInIds.add(Number(allocatedId));
        });
        const newlySelected = currentSelected.filter(
          (id) => id != null && !checkedInIds.has(Number(id)),
        );
        const continueFlow =
          isPartialCheckInStatus || isPartialCheckoutStatus || isCheckedInStatus;
        const maxSelectable = continueFlow
          ? Math.max(0, roomsNeededForType - checkedInIds.size)
          : roomsNeededForType;
        const selectedCount = currentSelected.filter((id) => id != null).length;
        const currentCount = continueFlow ? newlySelected.length : selectedCount;
        const emptySlotIndex = currentSelected.findIndex((id) => id == null);
        if (emptySlotIndex >= 0) {
          newState[roomTypeId] = [...currentSelected];
          newState[roomTypeId][emptySlotIndex] = roomId;
          return newState;
        }
        // At capacity: replace last non-checked-in selection (keeps guest stream; new room gets that allocation).
        if (roomsNeededForType > 0 && currentCount >= maxSelectable) {
          const replacePool = continueFlow ? newlySelected : currentSelected;
          const replaceable = replacePool.filter((id) => id != null && !checkedInIds.has(Number(id)));
          if (replaceable.length === 0) return prev;
          const toReplace = replaceable[replaceable.length - 1];
          newState[roomTypeId] = currentSelected.map((id) =>
            Number(id) === Number(toReplace) ? roomId : id,
          );
          // Move staged guest form from replaced room → new room (still UI-only until check-in).
          setPendingRoomGuests((prev) => {
            const staged = prev[toReplace] ?? prev[String(toReplace)];
            if (!staged) return prev;
            const next = { ...prev };
            delete next[toReplace];
            delete next[String(toReplace)];
            next[roomId] = staged;
            return next;
          });
          return newState;
        }
        newState[roomTypeId] = [...newState[roomTypeId], roomId];
        // Do not auto-open guest drawer on select — use compact/full Add Guest controls.
      }
      return newState;
    });
  }, [booking?.status, bookingItems, groupItemsBySequence]);

  const handleRoomSelection = useCallback((room, roomTypeId, isDisabled) => {
    if (isDisabled || !room?.id) return;
    const roomId = room.id;
    const currentSelected = selectedRoomIds[roomTypeId] || [];
    const isRoomSelected = currentSelected.some((id) => Number(id) === Number(roomId));

    if (isRoomSelected) {
      removeDirtyRoomPending(roomId);
      dirtyRoomQueueRef.current = dirtyRoomQueueRef.current.filter(
        (entry) => Number(entry.roomId) !== Number(roomId),
      );
      handleRoomToggle(roomId, roomTypeId);
      return;
    }

    // If at capacity, toggle will replace the last replaceable room — clear its dirty flag.
    const isItemFlagTrue = (raw) => {
      if (typeof raw === 'string') {
        return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
      }
      return raw === true || raw === 1;
    };
    const itemsForType = (bookingItems || []).filter(
      (item) => Number(item.room_type_id) === Number(roomTypeId),
    );
    const checkedInIds = new Set();
    itemsForType.forEach((item) => {
      if (isItemFlagTrue(item?.checkout) || !isItemFlagTrue(item?.checked_in)) return;
      const allocatedId = item?.allocated_room_id
        ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
        ?? null;
      if (allocatedId != null && allocatedId !== '') checkedInIds.add(Number(allocatedId));
    });
    const roomsNeededForType = Object.keys(
      groupItemsBySequence(itemsForType.filter((item) => !isItemFlagTrue(item?.checkout)))
    ).length || itemsForType.length;
    const bookingStatus = String(booking?.status || '').toLowerCase();
    const continueFlow =
      bookingStatus === 'partial_checkin'
      || bookingStatus === 'partial check-in'
      || bookingStatus === 'partial_checkout'
      || bookingStatus === 'partial checkout'
      || bookingStatus === 'checked_in';
    const newlySelected = currentSelected.filter((id) => id != null && !checkedInIds.has(Number(id)));
    const maxSelectable = continueFlow
      ? Math.max(0, roomsNeededForType - checkedInIds.size)
      : roomsNeededForType;
    const selectedCount = currentSelected.filter((id) => id != null).length;
    const currentCount = continueFlow ? newlySelected.length : selectedCount;
    if (roomsNeededForType > 0 && currentCount >= maxSelectable) {
      const replacePool = continueFlow ? newlySelected : currentSelected;
      const replaceable = replacePool.filter((id) => id != null && !checkedInIds.has(Number(id)));
      const toReplace = replaceable[replaceable.length - 1];
      if (toReplace != null) {
        removeDirtyRoomPending(toReplace);
        dirtyRoomQueueRef.current = dirtyRoomQueueRef.current.filter(
          (entry) => Number(entry.roomId) !== Number(toReplace),
        );
      }
    }

    handleRoomToggle(roomId, roomTypeId);
    if (isRoomDirty(room)) {
      enqueueDirtyRoomCleaning(roomId, roomTypeId);
    }
  }, [
    selectedRoomIds,
    handleRoomToggle,
    removeDirtyRoomPending,
    enqueueDirtyRoomCleaning,
    bookingItems,
    booking?.status,
  ]);

  const hasUncleanSelectedRooms = useMemo(() => {
    if (dirtyRoomsPendingClean.size > 0) return true;
    for (const roomArray of Object.values(selectedRoomIds)) {
      const ids = Array.isArray(roomArray) ? roomArray : [];
      for (const roomId of ids) {
        for (const group of availableRooms || []) {
          const room = (group.rooms || []).find((entry) => Number(entry.id) === Number(roomId));
          if (room && isRoomDirty(room)) return true;
        }
      }
    }
    return false;
  }, [selectedRoomIds, availableRooms, dirtyRoomsPendingClean]);

  const isCheckedOutBookingItem = useCallback((item) => {
    const raw = item?.checkout;
    if (typeof raw === 'string') {
      return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
    }
    return raw === true || raw === 1;
  }, []);

  const isCheckedInBookingItem = useCallback((item) => {
    const raw = item?.checked_in;
    if (typeof raw === 'string') {
      return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
    }
    return raw === true || raw === 1;
  }, []);

  const getItemAllocatedRoomId = useCallback((item) => {
    const roomId = item?.allocated_room_id
      ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
      ?? null;
    return roomId != null && roomId !== '' ? Number(roomId) : null;
  }, []);

  const pickPreferredGuestBookingItem = useCallback((items = []) => {
    const todayStr = moment().format('YYYY-MM-DD');
    const sortedItems = [...(items || [])].sort((a, b) => {
      const aDate = getBookingItemDateKey(a);
      const bDate = getBookingItemDateKey(b);
      if (aDate !== bDate) return aDate.localeCompare(bDate);
      return (a?.id || 0) - (b?.id || 0);
    });
    return (
      sortedItems.find((it) => !isCheckedOutBookingItem(it) && getBookingItemDateKey(it) === todayStr) ||
      sortedItems.find((it) => !isCheckedOutBookingItem(it) && getBookingItemDateKey(it) >= todayStr) ||
      sortedItems.find((it) => !isCheckedOutBookingItem(it)) ||
      sortedItems.find((it) => getBookingItemDateKey(it) >= todayStr) ||
      sortedItems[0] ||
      null
    );
  }, [getBookingItemDateKey, isCheckedOutBookingItem]);

  // ── resolveItemsForGuestUpdate: used by BookingGuestFormDrawer as resolveItemsToUpdate prop ──
  const resolveItemsForGuestUpdate = useCallback(async () => {
    let itemsToUpdate = [];

    if (editingRoomId) {
      // Try items already assigned to this room in selectedRooms
      itemsToUpdate = bookingItems.filter((item) => {
        const assignedRoomId = selectedRooms[item.id];
        return assignedRoomId != null && Number(assignedRoomId) === Number(editingRoomId);
      });

      if (itemsToUpdate.length === 0) {
        itemsToUpdate = bookingItems.filter((item) => {
          const allocatedId = getItemAllocatedRoomId(item);
          return allocatedId != null && Number(allocatedId) === Number(editingRoomId);
        });
      }

      if (itemsToUpdate.length === 0) {
        // Fallback: find by room-type / sequence
        let roomTypeId = null;
        for (const group of (availableRooms || [])) {
          const room = (group.rooms || []).find((r) => Number(r.id) === Number(editingRoomId));
          if (room) { roomTypeId = group.room_type.id; break; }
        }
        if (roomTypeId) {
          const itemsForType = bookingItems
            .filter((item) => Number(item.room_type_id) === Number(roomTypeId))
            .sort((a, b) => (a.room_sequence || 1) - (b.room_sequence || 1));
          const selectedForType =
            selectedRoomIds[roomTypeId] || selectedRoomIds[String(roomTypeId)] || [];
          const roomIndex = selectedForType.findIndex(
            (id) => Number(id) === Number(editingRoomId),
          );
          if (roomIndex >= 0) {
            const bySeq = groupItemsBySequence(itemsForType);
            const seqs = Object.keys(bySeq).sort((a, b) => String(a).localeCompare(String(b)));
            if (seqs[roomIndex]) itemsToUpdate = bySeq[seqs[roomIndex]] || [];
          }
        }
      }
    } else if (editingGuestItemId) {
      const item = bookingItems.find((i) => i.id === editingGuestItemId);
      if (!item) throw new Error('Booking item not found.');
      itemsToUpdate = [item];
    } else {
      throw new Error('No room or booking item selected.');
    }

    if (itemsToUpdate.length === 0) {
      throw new Error('No booking items found for this room.');
    }

    // Put preferred item first so BookingGuestFormDrawer PATCHes it as primary
    const preferred = pickPreferredGuestBookingItem(itemsToUpdate);
    if (preferred) {
      return [preferred, ...itemsToUpdate.filter((it) => it !== preferred)];
    }
    return itemsToUpdate;
  }, [
    editingRoomId, editingGuestItemId, bookingItems, selectedRooms, selectedRoomIds,
    availableRooms, groupItemsBySequence, pickPreferredGuestBookingItem, getItemAllocatedRoomId,
  ]);

  // ── roomLabelForGuest: chip shown in BookingGuestFormDrawer header ──
  const roomLabelForGuest = useMemo(() => {
    if (!editingRoomId || !availableRooms) return null;
    for (const group of availableRooms) {
      const room = (group.rooms || []).find((r) => r.id === editingRoomId);
      if (room) return `${group.room_type.name} - ${room.room_number}`;
    }
    return null;
  }, [editingRoomId, availableRooms]);


  const handleCheckIn = async () => {
    if (!bookingId) {
      setError('Booking ID is missing. Please refresh and try again.');
      return;
    }

    // Set submitting state early to prevent double-clicks
    setSubmitting(true);
    setError(null);
    setPaymentError('');

    if (hasUncleanSelectedRooms || housekeepingCleaningId) {
      setSubmitting(false);
      setError('Mark all selected rooms as clean before check-in.');
      return;
    }

    const allocatedRoomIds = new Set(
      (bookingItems || []).flatMap((item) => {
        if (item?.checkout === true) return [];
        const allocatedId = item?.allocated_room_id
          ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
          ?? null;
        return allocatedId != null && allocatedId !== '' ? [Number(allocatedId)] : [];
      }),
    );
    const heldSelectedRooms = [];
    (availableRooms || []).forEach((group) => {
      (group.rooms || []).forEach((room) => {
        const reason = getCheckInRoomHoldReason(room);
        if (!reason) return;
        if (allocatedRoomIds.has(Number(room.id)) && isStayWindowHoldReason(reason)) return;
        const isSelected = Object.values(selectedRoomIds || {}).some((ids) => (
          (ids || []).some((id) => id != null && Number(id) === Number(room.id))
        ));
        if (isSelected) heldSelectedRooms.push(room);
      });
    });
    if (heldSelectedRooms.length > 0) {
      const first = heldSelectedRooms[0];
      setSubmitting(false);
      setError(
        getCheckInRoomHoldTooltip(getCheckInRoomHoldReason(first), first.room_number)
        || 'Cannot check in to an occupied, pending-checkout, or blocked room.',
      );
      const contentBox = document.querySelector('[data-checkin-content]');
      if (contentBox) contentBox.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate that at least one room is selected (only for confirmed and partial_checkin bookings)
    const bookingStatus = String(booking?.status || '').toLowerCase();
    const isCheckedIn = bookingStatus === 'checked_in';
    const isPartialCheckIn = (
      bookingStatus === 'partial_checkin' ||
      bookingStatus === 'partial check-in' ||
      bookingStatus === 'partial_checkout' ||
      bookingStatus === 'partial checkout' ||
      (isCheckedIn && hasRoomsPendingCheckIn)
    );
    const isPartialCheckoutOnly =
      bookingStatus === 'partial_checkout' || bookingStatus === 'partial checkout';
    const canSelectRooms = bookingStatus === 'confirmed' || isPartialCheckIn;
    
    // Validate room selection per room type
    const nights = booking?.nights || 1;
    let hasError = false;
    let errorMessage = '';
    
    if (!isCheckedIn && !isPartialCheckIn) {
      // For confirmed bookings, validate per room type
      Object.keys(selectedRoomIds).forEach((roomTypeId) => {
        const selectedForType = selectedRoomIds[roomTypeId] || [];
        const selectedCountForType = selectedForType.filter((id) => id != null).length;
        
        // Calculate rooms needed for this room type
        const bookingItemsForType = bookingItems.filter(item => Number(item.room_type_id) === Number(roomTypeId));
        let roomsNeededForType = 0;
        if (nights > 0 && bookingItemsForType.length > 0) {
          roomsNeededForType = Math.round(bookingItemsForType.length / nights);
        } else if (bookingItemsForType.length > 0) {
          roomsNeededForType = bookingItemsForType.length;
        }
        
        if (selectedCountForType > roomsNeededForType) {
          hasError = true;
          const roomTypeName = availableRooms.find(rt => String(rt.room_type.id) === roomTypeId)?.room_type.name || 'this room type';
          errorMessage = `You can only select ${roomsNeededForType} room(s) for ${roomTypeName}. Please deselect ${selectedCountForType - roomsNeededForType} room(s).`;
        }
      });
      
      if (hasError) {
        setError(errorMessage);
        setSubmitting(false);
        const contentBox = document.querySelector('[data-checkin-content]');
        if (contentBox) {
          contentBox.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
      
      // Check if at least one room is selected overall
      const totalSelected = Object.values(selectedRoomIds).reduce(
        (sum, arr) => sum + (Array.isArray(arr) ? arr.filter((id) => id != null).length : 0),
        0
      );
      if (totalSelected === 0) {
        setError('Please select at least one room to check in.');
        setSubmitting(false);
        const contentBox = document.querySelector('[data-checkin-content]');
        if (contentBox) {
          contentBox.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
    } else if (isPartialCheckIn) {
      // Continue / partial check-in: validate against already checked-in rooms (not allocation)
      Object.keys(selectedRoomIds).forEach((roomTypeId) => {
        const selectedForType = selectedRoomIds[roomTypeId] || [];

        const checkedInRoomIdsForType = new Set();
        bookingItems.forEach((item) => {
          if (Number(item.room_type_id) !== Number(roomTypeId)) return;
          if (!isCheckedInBookingItem(item) || isCheckedOutBookingItem(item)) return;
          const roomId = getItemAllocatedRoomId(item);
          if (roomId != null) checkedInRoomIdsForType.add(roomId);
        });
        const checkedInForType = checkedInRoomIdsForType.size;
        
        // Calculate rooms needed for this type
        const bookingItemsForType = bookingItems.filter(item => Number(item.room_type_id) === Number(roomTypeId));
        let roomsNeededForType = 0;
        if (nights > 0 && bookingItemsForType.length > 0) {
          roomsNeededForType = Math.round(bookingItemsForType.length / nights);
        } else if (bookingItemsForType.length > 0) {
          roomsNeededForType = bookingItemsForType.length;
        }
        
        const newlySelectedRoomIds = selectedForType.filter(
          (roomId) => !checkedInRoomIdsForType.has(Number(roomId))
        );
        const uniqueNewlySelectedForType = new Set(newlySelectedRoomIds.map(Number)).size;
        
        const remainingForType = Math.max(0, roomsNeededForType - checkedInForType);
        
        if (uniqueNewlySelectedForType > remainingForType) {
          hasError = true;
          const roomTypeName = availableRooms.find(rt => String(rt.room_type.id) === String(roomTypeId))?.room_type?.name || 'this room type';
          errorMessage = `You can only select ${remainingForType} more room(s) for ${roomTypeName}. ${checkedInForType} room(s) are already checked in.`;
        }
      });
      
      if (hasError) {
        setError(errorMessage);
        setSubmitting(false);
        const contentBox = document.querySelector('[data-checkin-content]');
        if (contentBox) {
          contentBox.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
      
      // Partial checkout: any checkbox selection counts (including already-allocated in-house rooms).
      // Partial check-in only: require at least one newly allocated room.
      const totalSelectedPartial = Object.values(selectedRoomIds).reduce(
        (sum, arr) => sum + (Array.isArray(arr) ? arr.filter((id) => id != null).length : 0),
        0
      );
      if (isPartialCheckoutOnly) {
        if (totalSelectedPartial === 0) {
          setError('Please select at least one room using the checkbox before check-in.');
          setSubmitting(false);
          const contentBox = document.querySelector('[data-checkin-content]');
          if (contentBox) {
            contentBox.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return;
        }
      } else if (newlySelectedRoomsCount === 0) {
        setError('Please select at least one additional room to complete check-in.');
        setSubmitting(false);
        const contentBox = document.querySelector('[data-checkin-content]');
        if (contentBox) {
          contentBox.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
    }
      

    try {
      // Build allocations directly from the checkbox state so quick room changes
      // cannot submit stale selectedRooms mappings.
      const roomAllocations = {};
      Object.keys(selectedRoomIds).forEach((roomTypeId) => {
        const lockedCheckedInRoomIds = new Set();
        if (isPartialCheckIn) {
          bookingItems.forEach((item) => {
            if (Number(item.room_type_id) !== Number(roomTypeId)) return;
            if (!isCheckedInBookingItem(item) || isCheckedOutBookingItem(item)) return;
            const roomId = item?.allocated_room_id
              ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
              ?? null;
            if (roomId != null && roomId !== '') {
              lockedCheckedInRoomIds.add(Number(roomId));
            }
          });
        }
        const selectedIds = (selectedRoomIds[roomTypeId] || []).filter((roomId) =>
          roomId != null && !lockedCheckedInRoomIds.has(Number(roomId))
        );
        if (selectedIds.length === 0) return;

        const usedRoomIds = new Set();
        const pendingGroups = [];
        const itemsForType = bookingItems.filter((item) => {
          if (Number(item.room_type_id) !== Number(roomTypeId)) return false;
          if (isCheckedOutBookingItem(item)) return false;
          if (isPartialCheckIn && isCheckedInBookingItem(item)) return false;
          return true;
        });
        const groups = groupItemsBySequence(itemsForType);

        Object.values(groups).forEach((itemsForGroup) => {
          const currentRoomId = (itemsForGroup || [])
            .map((item) => item?.allocated_room_id
              ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
              ?? null)
            .find((roomId) => roomId != null && roomId !== '');
          const keepExistingRoomId = selectedIds.find(
            (selectedId) => Number(selectedId) === Number(currentRoomId)
          );

          if (keepExistingRoomId != null && !usedRoomIds.has(Number(keepExistingRoomId))) {
            usedRoomIds.add(Number(keepExistingRoomId));
            (itemsForGroup || []).forEach((item) => {
              roomAllocations[item.id] = keepExistingRoomId;
            });
            return;
          }

          pendingGroups.push(itemsForGroup || []);
        });

        pendingGroups.forEach((itemsForGroup) => {
          const nextRoomId = selectedIds.find((roomId) => !usedRoomIds.has(Number(roomId)));
          if (nextRoomId == null) return;
          usedRoomIds.add(Number(nextRoomId));
          itemsForGroup.forEach((item) => {
            roomAllocations[item.id] = nextRoomId;
          });
        });
      });
      
      // Verify mapping: all items with same (room_type, sequence) should have same room
      const mappingBySequence = {};
      Object.keys(roomAllocations).forEach(itemId => {
        const item = bookingItems.find(i => i.id === parseInt(itemId));
        if (item) {
          const key = `${item.room_type_id}_${item.room_sequence || 1}`;
          if (!mappingBySequence[key]) {
            mappingBySequence[key] = [];
          }
          mappingBySequence[key].push({
            itemId: item.id,
            roomId: roomAllocations[itemId],
            date: item.date || item.check_in_date,
            sequence: item.room_sequence || 1
          });
        }
      });
      
      // Check for consistency and duplicates
      Object.keys(mappingBySequence).forEach(key => {
        const items = mappingBySequence[key];
        const roomIds = [...new Set(items.map(i => i.roomId))];
        // Validation - roomIds.length > 1 would indicate inconsistency
      });
      
      // Check for duplicate room assignments across different (room_type, sequence) combinations
      const roomToSequences = {};
      Object.keys(mappingBySequence).forEach(key => {
        const items = mappingBySequence[key];
        if (items.length > 0) {
          const roomId = items[0].roomId;
          if (!roomToSequences[roomId]) {
            roomToSequences[roomId] = [];
          }
          roomToSequences[roomId].push(key);
        }
      });
      
      // Validate room allocations for new check-ins and partial check-ins
      // For partial check-in, we need at least one NEW room selected (not just already allocated ones)
      if (!isCheckedIn && !isPartialCheckIn && Object.keys(roomAllocations).length === 0) {
        setError('Please select at least one room to check in.');
        setSubmitting(false);
        const contentBox = document.querySelector('[data-checkin-content]');
        if (contentBox) {
          contentBox.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
      
      // For partial check-in, validate newly selected (not yet in-house) rooms
      if (isPartialCheckIn) {
        const alreadyCheckedInRoomIds = new Set();
        bookingItems.forEach((item) => {
          const itemCheckedIn = item.checked_in === true || item.checked_in === 1 || item.checked_in === 'true';
          if (!itemCheckedIn || isCheckedOutBookingItem(item)) return;
          const roomId = item.allocated_room_id
            || (item.allocated_room && (item.allocated_room.id || item.allocated_room.room_id))
            || null;
          if (roomId) {
            alreadyCheckedInRoomIds.add(Number(roomId));
          }
        });
        
        const newRoomIds = Object.values(roomAllocations).filter((roomId) =>
          roomId && !alreadyCheckedInRoomIds.has(Number(roomId))
        );
        const uniqueNewRoomCount = new Set(newRoomIds.map(Number)).size;
        
        if (uniqueNewRoomCount === 0 && Object.keys(roomAllocations).length === 0) {
          setError('Please select at least one additional room to complete check-in.');
          setSubmitting(false);
          const contentBox = document.querySelector('[data-checkin-content]');
          if (contentBox) {
            contentBox.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return;
        }
        
        if (uniqueNewRoomCount > remainingRoomsNeeded) {
          setError(`You can only select ${remainingRoomsNeeded} more room(s). ${allocatedRoomsCount} room(s) are already checked in.`);
          setSubmitting(false);
          const contentBox = document.querySelector('[data-checkin-content]');
          if (contentBox) {
            contentBox.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return;
        }
      }

      if (bookingStatus === 'confirmed') {
        const allowedPurpose = new Set(PURPOSE_OF_STAY_OPTIONS.map((o) => o.value));
        if (!purposeOfStay || !allowedPurpose.has(purposeOfStay)) {
          setError('Purpose of stay is required.');
          setSubmitting(false);
          const contentBox = document.querySelector('[data-checkin-content]');
          if (contentBox) {
            contentBox.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return;
        }
        if (purposeOfStay === 'other' && !(purposeOfStayNote || '').trim()) {
          setError('Please describe the purpose when "Other" is selected.');
          setSubmitting(false);
          const contentBox = document.querySelector('[data-checkin-content]');
          if (contentBox) {
            contentBox.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return;
        }
      }
      
      if (isPartialCheckIn) {
        const pendingAllocationsToClear = {};
        bookingItems.forEach((item) => {
          if (!item || isCheckedOutBookingItem(item) || isCheckedInBookingItem(item)) return;
          const itemKey = String(item.id);
          const selectedRoomId = roomAllocations[itemKey];
          const currentRoomId = item.allocated_room_id
            || (item.allocated_room && (item.allocated_room.id || item.allocated_room.room_id))
            || null;
          if (selectedRoomId == null && currentRoomId) {
            pendingAllocationsToClear[itemKey] = null;
          }
        });
        if (Object.keys(pendingAllocationsToClear).length > 0) {
          await assignRooms(bookingId, pendingAllocationsToClear);
        }
      }

      // Payment collection is now handled through the separate payment drawer
      // No inline payment collection during check-in
      const paymentData = { collect_payment: false };

      const purposeFields =
        bookingStatus === 'confirmed'
          ? {
              purpose_of_stay: purposeOfStay,
              purpose_of_stay_note:
                purposeOfStay === 'other' ? (purposeOfStayNote || '').trim() : '',
            }
          : null;

      const result = await processCheckIn(bookingId, roomAllocations, paymentData, purposeFields);
      if (result.success) {
        // Persist UI-staged per-room guests only after successful check-in.
        const pendingEntries = Object.entries(pendingRoomGuests || {});
        for (const [roomId, formData] of pendingEntries) {
          const guestData = (() => {
            if (!formData) return null;
            let phoneNumber = null;
            let countryCode = null;
            if (formData.phone && String(formData.phone).trim()) {
              phoneNumber = String(formData.phone).trim().replace(/\D/g, '');
              countryCode = formData.phone_country_code || '+91';
              if (!String(countryCode).startsWith('+')) countryCode = `+${countryCode}`;
            }
            let firstName = formData.first_name;
            let middleName = formData.middle_name;
            let lastName = formData.last_name;
            if (formData.name && String(formData.name).trim()) {
              const parsed = parseFullName(formData.name);
              firstName = parsed.first_name;
              middleName = parsed.middle_name;
              lastName = parsed.last_name;
            }
            if (!firstName || !String(firstName).trim()) return null;
            const payload = { first_name: String(firstName).trim() };
            if (middleName && String(middleName).trim()) payload.middle_name = String(middleName).trim();
            if (lastName && String(lastName).trim()) payload.last_name = String(lastName).trim();
            if (formData.email && String(formData.email).trim()) payload.email = String(formData.email).trim();
            if (phoneNumber) payload.phone = phoneNumber;
            if (countryCode) payload.country_code = String(countryCode).trim();
            if (formData.title && String(formData.title).trim()) payload.title = String(formData.title).trim();
            if (formData.address && String(formData.address).trim()) payload.address = String(formData.address).trim();
            if (formData.city && String(formData.city).trim()) payload.city = String(formData.city).trim();
            if (formData.state && String(formData.state).trim()) payload.state = String(formData.state).trim();
            if (formData.country && String(formData.country).trim()) payload.country = String(formData.country).trim();
            if (formData.zipcode && String(formData.zipcode).trim()) payload.pincode = String(formData.zipcode).trim();
            payload.dob = formData.dob ? String(formData.dob).slice(0, 10) : '';
            if (formData.gender && String(formData.gender).trim()) {
              payload.gender = String(formData.gender).trim();
            }
            const docType = formData.document_type && formData.document_type !== 'no_document'
              ? formData.document_type
              : '';
            if (docType) {
              payload.document_type = docType;
              if (formData.id_number && String(formData.id_number).trim()) {
                payload.id_number = normalizeDocumentIdForSubmit(docType, formData.id_number);
              }
            }
            if (formData.id_front && typeof formData.id_front === 'string' && formData.id_front.startsWith('data:image')) {
              payload.id_front = formData.id_front;
            }
            if (formData.id_back && typeof formData.id_back === 'string' && formData.id_back.startsWith('data:image')) {
              payload.id_back = formData.id_back;
            }
            return payload;
          })();
          if (!guestData) continue;
          const itemIds = Object.entries(roomAllocations)
            .filter(([, rid]) => Number(rid) === Number(roomId))
            .map(([itemId]) => itemId);
          for (const itemId of itemIds) {
            try {
              await api.patch(`/booking/api/${bookingId}/`, {
                booking_item_id: itemId,
                guest: guestData,
              });
            } catch {
              // Check-in already succeeded; continue remaining guests.
            }
          }
        }
        setPendingRoomGuests({});

        // If it's a room update for already checked-in booking, show success message and don't close
        if (result.is_room_update) {
          const message = result.message || 'Rooms updated successfully.';
          setSuccessMessage(message);
          setError(null);
          
          // Refresh available rooms to show updated allocations and payment info
          await fetchAvailableRooms();
          
          // Update booking state with response data if available (includes updated payment info)
          // Do this after fetchAvailableRooms to ensure we have the latest data
          if (result.booking) {
            setBooking(prevBooking => {
              const updated = {
                ...prevBooking,
                ...result.booking,
              };
              // Ensure payment fields are updated from response (these are the most recent)
              if (result.booking.advance_amount !== undefined) {
                updated.advance_amount = result.booking.advance_amount;
              }
              if (result.booking.remaining_amount !== undefined) {
                updated.remaining_amount = result.booking.remaining_amount;
              }
              if (result.booking.total_amount !== undefined) {
                updated.total_amount = result.booking.total_amount;
              }
              return updated;
            });
          }
          if (onCheckInSuccess) {
            onCheckInSuccess(result.booking);
          }
        } else {
          // New check-in - close drawer
          if (onCheckInSuccess) {
            onCheckInSuccess(result.booking);
          }
          onClose();
        }
      }
    } catch (err) {
      const errorData = err.response?.data || {};
      const errorMsg = errorData.error || errorData.message || err.message || 'Failed to process check-in. Please try again.';
      setError(errorMsg);
      // Scroll to top to show error
      const contentBox = document.querySelector('[data-checkin-content]');
      if (contentBox) {
        contentBox.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Memoize booking status calculations (must be before room-selection memos that depend on them)
  const bookingStatus = useMemo(() => String(booking?.status || '').toLowerCase(), [booking?.status]);
  const isCheckedOut = useMemo(() => booking?.status === 'checked_out', [booking?.status]);
  const isPartialCheckout = useMemo(
    () => bookingStatus === 'partial_checkout' || bookingStatus === 'partial checkout',
    [bookingStatus],
  );
  const isCheckedIn = useMemo(() => bookingStatus === 'checked_in', [bookingStatus]);
  const isPartialCheckInOnly = useMemo(
    () => bookingStatus === 'partial_checkin' || bookingStatus === 'partial check-in',
    [bookingStatus],
  );
  const isConfirmed = useMemo(() => bookingStatus === 'confirmed', [bookingStatus]);
  const isReserved = useMemo(() => bookingStatus === 'reserved', [bookingStatus]);
  const roomTypeGroupsToRender = useMemo(() => {
    if (availableRooms && availableRooms.length > 0) return availableRooms;
    if (!isReserved || !bookingItems?.length) return [];
    const byType = new Map();
    bookingItems.forEach((item) => {
      const id = Number(item.room_type_id ?? item.room_type?.id);
      if (!Number.isFinite(id) || byType.has(id)) return;
      byType.set(id, {
        room_type: {
          id,
          name: item.room_type?.name || item.room_type_name || 'Room',
        },
        rooms: [],
      });
    });
    return Array.from(byType.values());
  }, [availableRooms, isReserved, bookingItems]);

  // Memoize expensive computations
  const totalRoomsNeeded = useMemo(() => booking?.total_rooms || 0, [booking?.total_rooms]);

  // Streams still needing check-in: a stream is pending unless ALL its active items are checked_in.
  const hasRoomsPendingCheckIn = useMemo(() => {
    if (!bookingItems?.length) return false;
    const streamFullyCheckedIn = new Map();
    bookingItems.forEach((item) => {
      if (item?.room_type_id == null) return;
      if (isCheckedOutBookingItem(item)) return;
      const streamKey = `${item.room_type_id}_${item.room_sequence || 1}_${item.allocated_room_id || item.id}`;
      const itemCheckedIn = isCheckedInBookingItem(item);
      const prev = streamFullyCheckedIn.get(streamKey);
      streamFullyCheckedIn.set(
        streamKey,
        prev === undefined ? itemCheckedIn : prev && itemCheckedIn,
      );
    });
    for (const fullyCheckedIn of streamFullyCheckedIn.values()) {
      if (!fullyCheckedIn) return true;
    }
    return false;
  }, [bookingItems, isCheckedInBookingItem, isCheckedOutBookingItem]);

  /** Partial / continue check-in — shared check-in submit and allocation logic. */
  const isPartialCheckIn = useMemo(
    () => isPartialCheckInOnly || isPartialCheckout || (isCheckedIn && hasRoomsPendingCheckIn),
    [isPartialCheckInOnly, isPartialCheckout, isCheckedIn, hasRoomsPendingCheckIn],
  );

  // Partial / continue check-in UI (not initial confirmed check-in).
  // Includes checked_in status when some streams are still booked (stale rollup).
  const isContinueCheckInFlow = useMemo(
    () =>
      isPartialCheckInOnly
      || ((isCheckedIn || isPartialCheckout) && hasRoomsPendingCheckIn),
    [isPartialCheckInOnly, isCheckedIn, isPartialCheckout, hasRoomsPendingCheckIn],
  );

  // Allow continuing check-in when booking is partial, or fully-status'd but items still pending.
  const allowContinueCheckIn = useMemo(
    () => isConfirmed || isPartialCheckIn || isContinueCheckInFlow,
    [isConfirmed, isPartialCheckIn, isContinueCheckInFlow],
  );
  
  // Already in-house rooms (checked_in=True). Allocation alone does not count.
  const allocatedRoomsCount = useMemo(() => {
    if (!isPartialCheckIn && !isContinueCheckInFlow) return 0;
    const roomIds = new Set();
    if (bookingItems?.length > 0) {
      bookingItems.forEach((item) => {
        if (!isCheckedInBookingItem(item) || isCheckedOutBookingItem(item)) return;
        const roomId = getItemAllocatedRoomId(item);
        if (roomId != null) roomIds.add(roomId);
      });
    }
    return roomIds.size;
  }, [
    isPartialCheckIn,
    isContinueCheckInFlow,
    bookingItems,
    isCheckedInBookingItem,
    isCheckedOutBookingItem,
    getItemAllocatedRoomId,
  ]);
  
  // Calculate remaining rooms needed (for partial check-in)
  const remainingRoomsNeeded = useMemo(() => {
    if (isPartialCheckIn || isContinueCheckInFlow) {
      return Math.max(0, totalRoomsNeeded - allocatedRoomsCount);
    }
    return totalRoomsNeeded;
  }, [isPartialCheckIn, isContinueCheckInFlow, totalRoomsNeeded, allocatedRoomsCount]);
  
  // Newly selected for check-in: not already in-house (may still be pre-allocated).
  const newlySelectedRoomsCount = useMemo(() => {
    const continuePartial = isPartialCheckIn || isContinueCheckInFlow;
    if (!continuePartial) {
      // For confirmed bookings, return total selected
      let total = 0;
      Object.values(selectedRoomIds).forEach((roomArray) => {
        if (Array.isArray(roomArray)) {
          total += roomArray.filter((id) => id != null).length;
        } else if (roomArray && typeof roomArray.size === 'number') {
          total += roomArray.size;
        }
      });
      return total;
    }
    
    const newRoomIds = new Set();
    Object.values(selectedRoomIds).forEach((roomArray) => {
      const arr = Array.isArray(roomArray) ? roomArray : (roomArray && typeof roomArray.forEach === 'function' ? Array.from(roomArray) : []);
      arr.forEach((roomId) => {
        if (roomId == null) return;
        const alreadyCheckedIn = bookingItems.some((item) => {
          if (!isCheckedInBookingItem(item) || isCheckedOutBookingItem(item)) return false;
          const itemRoomId = getItemAllocatedRoomId(item);
          return itemRoomId != null && itemRoomId === Number(roomId);
        });
        if (!alreadyCheckedIn) {
          newRoomIds.add(Number(roomId));
        }
      });
    });
    return newRoomIds.size;
  }, [
    selectedRoomIds,
    isPartialCheckIn,
    isContinueCheckInFlow,
    bookingItems,
    allocatedRoomsCount,
    isCheckedInBookingItem,
    isCheckedOutBookingItem,
    getItemAllocatedRoomId,
  ]);
  
  const totalRoomsSelected = useMemo(() => {
    let total = 0;
    Object.values(selectedRoomIds).forEach((roomArray) => {
      if (Array.isArray(roomArray)) {
        total += roomArray.filter((id) => id != null).length;
      } else if (roomArray && typeof roomArray.size === 'number') {
        // Handle Set objects
        total += roomArray.size;
      }
    });
    return total;
  }, [selectedRoomIds]);

  const hasSelectedRooms = useMemo(() => {
    const st = String(booking?.status || '').toLowerCase();
    const partialCheckoutOnly = st === 'partial_checkout' || st === 'partial checkout';
    // Partial checkout: no pre-selected rooms — enable Check In only after explicit checkbox selection
    if (partialCheckoutOnly) {
      return totalRoomsSelected > 0;
    }
    const partialFlow =
      st === 'partial_checkin' ||
      st === 'partial check-in' ||
      partialCheckoutOnly ||
      (st === 'checked_in' && hasRoomsPendingCheckIn);
    const roomsToCheck = partialFlow ? newlySelectedRoomsCount : totalRoomsSelected;
    return roomsToCheck > 0;
  }, [booking?.status, totalRoomsSelected, newlySelectedRoomsCount, hasRoomsPendingCheckIn]);

  const selectedHeldRoom = useMemo(() => {
    const selectedIds = new Set();
    Object.values(selectedRoomIds || {}).forEach((ids) => {
      (ids || []).forEach((id) => {
        if (id != null) selectedIds.add(Number(id));
      });
    });
    if (selectedIds.size === 0) return null;
    const allocatedIds = new Set(
      (bookingItems || []).flatMap((item) => {
        if (item?.checkout === true) return [];
        const allocatedId = item?.allocated_room_id
          ?? (item?.allocated_room && (item.allocated_room.id ?? item.allocated_room.room_id))
          ?? null;
        return allocatedId != null && allocatedId !== '' ? [Number(allocatedId)] : [];
      }),
    );
    for (const group of availableRooms || []) {
      for (const room of group.rooms || []) {
        if (!selectedIds.has(Number(room.id))) continue;
        const reason = getCheckInRoomHoldReason(room);
        if (!reason) continue;
        if (allocatedIds.has(Number(room.id)) && isStayWindowHoldReason(reason)) continue;
        return room;
      }
    }
    return null;
  }, [availableRooms, selectedRoomIds, bookingItems]);
  
  const allRoomsSelected = useMemo(() => {
    if (isPartialCheckIn || isContinueCheckInFlow) {
      // Continue check-in: newly selected covers remaining not-yet-checked-in rooms
      return remainingRoomsNeeded > 0 && newlySelectedRoomsCount >= remainingRoomsNeeded;
    }
    // For confirmed bookings, check if total selected >= total needed
    return totalRoomsNeeded > 0 && totalRoomsSelected >= totalRoomsNeeded;
  }, [
    isPartialCheckIn,
    isContinueCheckInFlow,
    totalRoomsNeeded,
    totalRoomsSelected,
    remainingRoomsNeeded,
    newlySelectedRoomsCount,
  ]);
  
  const isPartialRoomSelection = useMemo(() => 
    hasSelectedRooms && !allRoomsSelected,
    [hasSelectedRooms, allRoomsSelected]
  );
  
  const selectedRoomsByType = useMemo(() => {
    const result = {};
    Object.keys(selectedRoomIds).forEach((roomTypeId) => {
      const selectedArray = selectedRoomIds[roomTypeId];
      result[roomTypeId] = Array.isArray(selectedArray)
        ? selectedArray.filter((id) => id != null).length
        : 0;
    });
    return result;
  }, [selectedRoomIds]);

  // Trim over-selected rooms (e.g. booked rooms used to bypass the cap).
  useEffect(() => {
    if (!isContinueCheckInFlow && !isConfirmed) return;
    if (!bookingItems?.length) return;
    setSelectedRoomIds((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((typeId) => {
        const selected = Array.isArray(next[typeId]) ? next[typeId] : [];
        if (selected.length === 0) return;
        const itemsForType = bookingItems.filter(
          (item) => Number(item.room_type_id) === Number(typeId),
        );
        const roomsNeeded = Object.keys(
          groupItemsBySequence(itemsForType.filter((item) => !isCheckedOutBookingItem(item)))
        ).length || itemsForType.length;
        if (roomsNeeded <= 0) return;
        const checkedInIds = new Set();
        itemsForType.forEach((item) => {
          if (!isCheckedInBookingItem(item) || isCheckedOutBookingItem(item)) return;
          const roomId = getItemAllocatedRoomId(item);
          if (roomId != null) checkedInIds.add(roomId);
        });
        const maxNew = isContinueCheckInFlow
          ? Math.max(0, roomsNeeded - checkedInIds.size)
          : roomsNeeded;
        const keptCheckedIn = selected.filter((id) => checkedInIds.has(Number(id)));
        const keptNew = selected
          .filter((id) => !checkedInIds.has(Number(id)))
          .slice(0, maxNew);
        const trimmed = isContinueCheckInFlow
          ? [...keptCheckedIn, ...keptNew]
          : selected.slice(0, roomsNeeded);
        if (
          trimmed.length !== selected.length
          || trimmed.some((id, idx) => Number(id) !== Number(selected[idx]))
        ) {
          next[typeId] = trimmed;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [
    isContinueCheckInFlow,
    isConfirmed,
    bookingItems,
    isCheckedInBookingItem,
    isCheckedOutBookingItem,
    getItemAllocatedRoomId,
  ]);

  const purposeOfStayValid = useMemo(() => {
    if (!isConfirmed || !hasSelectedRooms) return true;
    const allowed = new Set(PURPOSE_OF_STAY_OPTIONS.map((o) => o.value));
    if (!purposeOfStay || !allowed.has(purposeOfStay)) return false;
    if (purposeOfStay === 'other') {
      return Boolean((purposeOfStayNote || '').trim());
    }
    return true;
  }, [isConfirmed, hasSelectedRooms, purposeOfStay, purposeOfStayNote]);

  const canCheckInByDate = useMemo(() => {
    if (!booking?.check_in_date) return true;
    const today = moment().startOf('day');
    const checkInDate = moment(booking.check_in_date).startOf('day');
    if (!checkInDate.isValid()) return true;
    return !today.isBefore(checkInDate);
  }, [booking?.check_in_date]);
  const stayCheckInDisplay = useMemo(() => resolveStayCheckInDisplay(booking), [booking]);
  const stayCheckOutDisplay = useMemo(() => resolveStayCheckOutDisplay(booking), [booking]);
  const actualCheckInDisplay = useMemo(() => resolveActualCheckInDisplay(booking), [booking]);
  const actualCheckOutDisplay = useMemo(() => resolveActualCheckOutDisplay(booking), [booking]);
  const isCancelledStatus = useMemo(
    () =>
      bookingStatus === 'cancelled' ||
      bookingStatus === 'canceled' ||
      bookingStatus === 'no_show' ||
      bookingStatus === 'no show',
    [bookingStatus]
  );
  // Room selection for confirmed / partial check-in / incomplete checked-in bookings
  const canSelectRooms = useMemo(() => allowContinueCheckIn, [allowContinueCheckIn]);

  // Memoize allocated room IDs calculation
  const allocatedRoomIds = useMemo(() => {
    const roomIds = new Set();
    // Include allocated rooms for checked-in, checked-out, partial_checkin, and partial_checkout bookings
    if (isCheckedIn || isCheckedOut || isPartialCheckIn || isPartialCheckout) {
      // Get from bookingItems - this is the source of truth for allocated rooms
      // For partial_checkout, include ALL allocated rooms (including checked-out ones)
      if (bookingItems?.length > 0) {
        bookingItems.forEach((item) => {
          // Include all allocated rooms regardless of checkout status
          const roomId = item.allocated_room_id 
            || (item.allocated_room && (item.allocated_room.id || item.allocated_room.room_id))
            || null;
          if (roomId) {
            roomIds.add(Number(roomId));
          }
        });
      }
      
      // Get from selectedRooms (for consistency)
      if (selectedRooms && Object.keys(selectedRooms).length > 0) {
        Object.values(selectedRooms).forEach((roomId) => {
          if (roomId) {
            roomIds.add(Number(roomId));
          }
        });
      }
      
      // Get from selectedRoomIds (for consistency)
      if (selectedRoomIds && Object.keys(selectedRoomIds).length > 0) {
        Object.values(selectedRoomIds).forEach((roomIdArray) => {
          if (Array.isArray(roomIdArray)) {
            roomIdArray.forEach((roomId) => {
              if (roomId) {
                roomIds.add(Number(roomId));
              }
            });
          }
        });
      }
    }
    return roomIds;
  }, [isCheckedIn, isCheckedOut, isPartialCheckIn, isPartialCheckout, bookingItems, selectedRooms, selectedRoomIds]);

  // Detect sequences where allocated room changes across dates for the same (room_type, room_sequence)
  const changedSequenceKeys = useMemo(() => {
    const sequenceToRooms = new Map();
    (bookingItems || []).forEach((item) => {
      const roomTypeId = item.room_type_id ?? item.room_type?.id;
      if (!roomTypeId) return;
      const seq = item.room_sequence || 1;
      const key = `${roomTypeId}_${seq}`;
      const roomId =
        item.allocated_room_id ||
        (item.allocated_room && (item.allocated_room.id || item.allocated_room.room_id)) ||
        null;
      if (!roomId) return;
      if (!sequenceToRooms.has(key)) sequenceToRooms.set(key, new Set());
      sequenceToRooms.get(key).add(Number(roomId));
    });
    const changed = new Set();
    sequenceToRooms.forEach((roomIds, key) => {
      if (roomIds.size > 1) changed.add(key);
    });
    return changed;
  }, [bookingItems]);

  // Detect sequences where room type changes across dates (upgrade flow)
  const upgradedRoomTypeKeys = useMemo(() => {
    const sequenceRoomTypeStartDate = new Map();
    const sequenceItems = new Map();
    const explicitUpgradeCandidates = new Set();
    const getItemDateKey = (item) => {
      if (!item?.date) return '';
      return moment.isMoment(item.date) ? item.date.format('YYYY-MM-DD') : String(item.date).slice(0, 10);
    };
    (bookingItems || []).forEach((item) => {
      const roomTypeId = Number((item.room_type_id ?? item.room_type?.id) || 0);
      if (!roomTypeId) return;
      const seq = Number(item.room_sequence || 1);
      if (!sequenceItems.has(seq)) sequenceItems.set(seq, []);
      sequenceItems.get(seq).push(item);
      const upgradeFromTypeId = Number(
        (item.upgraded_from_room_type_id ?? item.upgraded_from_room_type?.id) || 0
      );
      if (upgradeFromTypeId && upgradeFromTypeId !== roomTypeId) {
        explicitUpgradeCandidates.add(`${seq}_${roomTypeId}`);
      }
      const itemDate = getItemDateKey(item);
      if (!itemDate) return;
      if (!sequenceRoomTypeStartDate.has(seq)) sequenceRoomTypeStartDate.set(seq, new Map());
      const perType = sequenceRoomTypeStartDate.get(seq);
      const currentStart = perType.get(roomTypeId);
      if (!currentStart || itemDate < currentStart) {
        perType.set(roomTypeId, itemDate);
      }
    });

    // Always trust explicit lineage markers from backend payload.
    // Fallback heuristics below only add missing candidates when marker is absent.
    const upgraded = new Set(explicitUpgradeCandidates);
    sequenceRoomTypeStartDate.forEach((perType, seq) => {
      const dates = [...perType.values()].filter(Boolean);
      if (dates.length <= 1) return;
      const sequenceStartDate = dates.reduce((minDate, d) => (d < minDate ? d : minDate), dates[0]);
      const seqItems = sequenceItems.get(seq) || [];
      perType.forEach((typeStartDate, rtId) => {
        // Mark fallback upgrade only when:
        // 1) room-type starts later than baseline, and
        // 2) there is prior checked-out history in same sequence with a different room-type.
        // This keeps chip focused on post-check-in (mid-stay) upgrades.
        const hasPriorCheckedOutHistory = seqItems.some((it) => {
          const itTypeId = Number((it.room_type_id ?? it.room_type?.id) || 0);
          if (!itTypeId || itTypeId === rtId) return false;
          const itDate = getItemDateKey(it);
          if (!itDate || itDate >= typeStartDate) return false;
          return isCheckedOutBookingItem(it);
        });
        const key = `${seq}_${rtId}`;
        const isExplicitCandidate = explicitUpgradeCandidates.has(key);
        if (isExplicitCandidate) {
          upgraded.add(key);
        } else if (typeStartDate > sequenceStartDate && hasPriorCheckedOutHistory) {
          upgraded.add(key);
        }
      });
    });

    return upgraded;
  }, [bookingItems, isCheckedOutBookingItem]);

  const roomTypeSortOrderById = useMemo(() => {
    const fromItems = buildRoomTypeSortOrderMap(bookingItems);
    const fromAvailable = buildRoomTypeSortOrderMap(
      (availableRooms || []).map((group) => group?.room_type).filter(Boolean),
    );
    return { ...fromAvailable, ...fromItems };
  }, [bookingItems, availableRooms]);

  const round2 = (x) => Math.round(Number(x) * 100) / 100;
  const formatCurrency = (amount, currency = 'INR') => {
    if (amount == null || amount === '' || Number(amount) === 0) {
      return `${getBookingCurrencySymbol(currency)}0.00`;
    }
    const value = round2(amount);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };
  const bookingCurrency = booking?.currency || 'INR';
  const bookingCurrencySymbol = getBookingCurrencySymbol(bookingCurrency);

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
        },
      }}
    >
      <Box ref={drawerContainerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            p: 1.5,
            borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <BookingDrawerHeaderIdentity
            icon={<CheckInIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24, flexShrink: 0 }} />}
            title="Check-In"
            booking={booking}
            bookingId={bookingId}
            onNavigate={onClose}
            isDark={isDark}
            drawerTheme={drawerTheme}
            label={booking?.code}
          />
          {canViewBookingAudit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
            onClick={() => setAuditDrawerOpen(true)}
            disabled={!auditBookingId}
            sx={{
              borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
              color: CUSTOM_BORDER_COLOR,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '999px',
              px: 1.5,
              py: 0.3,
              minWidth: 84,
              flexShrink: 0,
              '&:hover': {
                borderColor: CUSTOM_BORDER_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: isDark ? drawerTheme.surfaceBorder : '#d0d0d0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            Audit
          </Button>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }} data-checkin-content>
          {booking && (
            <>
              {/* Booking Information - Simplified */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                  borderRadius: '12px',
                  position: 'relative',
                }}
              >
                {/* Channel Image and Booking Status - Top Right */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {/* Channel Image - Display only if channel is not null */}
                  {(booking?.channel_data || booking?.channel) && (
                    booking?.channel_data ? (
                      <ChannelImage channel={booking.channel_data} size={32} />
                    ) : (
                      <ChannelImage isPMS={true} size={32} />
                    )
                  )}
                  
                  {/* Booking Status Chip */}
                  {booking.status && (
                    <Chip
                      label={booking.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return isDark ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)';
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return isDark ? 'rgba(76, 175, 80, 0.22)' : 'rgba(76, 175, 80, 0.14)';
                          } else if (statusLower === 'checked_out') {
                            return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)';
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return isDark ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)';
                          }
                          return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                        })(),
                        color: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return isDark ? '#64b5f6' : '#1976d2';
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return isDark ? '#a5d6a7' : '#2e7d32';
                          } else if (statusLower === 'checked_out') {
                            return isDark ? '#bdbdbd' : '#616161';
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return isDark ? '#ef5350' : '#c62828';
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return isDark ? '#ffb74d' : '#e65100';
                          }
                          return isDark ? '#bdbdbd' : '#616161';
                        })(),
                        border: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return `1px solid ${isDark ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.25)'}`;
                          } else if (
                            statusLower === 'checked_in' ||
                            statusLower === 'partial_checkin' ||
                            statusLower === 'partial_checkout'
                          ) {
                            return `1px solid ${isDark ? 'rgba(76, 175, 80, 0.55)' : 'rgba(76, 175, 80, 0.4)'}`;
                          } else if (statusLower === 'checked_out') {
                            return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return `1px solid ${isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'}`;
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return `1px solid ${isDark ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'}`;
                          }
                          return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                        })(),
                        backdropFilter: 'blur(10px)',
                        boxShadow: isDark 
                          ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  )}
                  {isSameDayStay(booking) && (
                    <Chip
                      label="Hourly"
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: isDark ? 'rgba(255, 167, 38, 0.2)' : 'rgba(255, 167, 38, 0.12)',
                        color: isDark ? '#ffb74d' : '#ef6c00',
                        border: `1px solid ${isDark ? 'rgba(255, 183, 77, 0.45)' : 'rgba(239, 108, 0, 0.35)'}`,
                      }}
                    />
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  {/* Guest Name with Booking Source */}
                  {booking.guest_name && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                            Guest Name:
                          </Typography>
                          <DrawerGuestFieldLink
                            isDark={isDark}
                            booking={booking}
                            guest={booking.guest}
                            guestId={booking?.guest_id ?? booking?.guest?.id}
                            variant="body2"
                            onNavigate={onClose}
                            sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                          >
                            {booking.guest_name}
                          </DrawerGuestFieldLink>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Mobile Number */}
                  {(booking.guest_phone || booking.phone) && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                          Mobile Number:
                        </Typography>
                        <DrawerGuestFieldLink
                          isDark={isDark}
                          booking={booking}
                          guest={booking.guest}
                          guestId={booking?.guest_id ?? booking?.guest?.id}
                          variant="body2"
                          onNavigate={onClose}
                          sx={{ fontWeight: 500, fontSize: '0.85rem' }}
                        >
                          {booking.guest_phone || booking.phone || '—'}
                        </DrawerGuestFieldLink>
                      </Box>
                    </Grid>
                  )}

                  {/* Check-in Information */}
                  <Grid item xs={12} sm={6}>
                    <Box
                        sx={{
                        p: 1,
                        borderRadius: '8px',
                        background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        minHeight: booking.check_in_time ? 'auto' : '72px',
                          display: 'flex',
                        flexDirection: 'column',
                        }}
                      >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <CheckInIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                        Check-In
                      </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                        {stayCheckInDisplay.date || '—'}
                      </Typography>
                      {stayCheckInDisplay.time ? (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                          {stayCheckInDisplay.time}
                        </Typography>
                      ) : (
                        <Box sx={{ height: '16px' }} />
                      )}
                    </Box>
                    <ActualStayDateChips actual={actualCheckInDisplay} label="Actual check-in" isDark={isDark} />
                  </Grid>

                  {/* Check-out Information */}
                  <Grid item xs={12} sm={6}>
                    <Box
                        sx={{
                        p: 1,
                        borderRadius: '8px',
                        background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        minHeight: '72px',
                          display: 'flex',
                        flexDirection: 'column',
                        }}
                      >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <CheckOutIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                        Check-Out
                      </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                        {stayCheckOutDisplay.date || '—'}
                      </Typography>
                      {stayCheckOutDisplay.time ? (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                          {stayCheckOutDisplay.time}
                        </Typography>
                      ) : (
                        <Box sx={{ height: '16px' }} />
                      )}
                    </Box>
                    <ActualStayDateChips actual={actualCheckOutDisplay} label="Actual check-out" isDark={isDark} />
                  </Grid>

                  {/* Nights / Hourly and Rooms */}
                  <Grid item xs={6}>
                    <StayDurationChip
                      booking={booking}
                      isDark={isDark}
                      accentColor={CUSTOM_BORDER_COLOR}
                      captionSx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                        Number of Rooms:
                      </Typography>
                      {(() => {
                        const detailItems = bookingItems || [];
                        const streamKeys = new Set();
                        const allocatedStreamKeys = new Set();
                        detailItems.forEach((item) => {
                          if (item?.checkout === true) return;
                          const rtId = item.room_type?.id || item.room_type_id;
                          if (!rtId) return;
                          const key = `${rtId}_${item.room_sequence || 1}_${item.allocated_room_id || item.id}`;
                          streamKeys.add(key);
                          const roomId = item.allocated_room_id
                            || (item.allocated_room && (item.allocated_room.id || item.allocated_room.room_id));
                          if (roomId) allocatedStreamKeys.add(key);
                        });
                        const totalRooms = Math.max(
                          Number(booking.total_rooms || booking.rooms_count || booking.rooms || 0) || 0,
                          streamKeys.size,
                        );
                        const allocatedRooms = allocatedStreamKeys.size;
                        const showPartial = totalRooms > 0 && allocatedRooms > 0 && allocatedRooms < totalRooms;
                        return (
                          <Tooltip
                            title={showPartial
                              ? `${allocatedRooms} of ${totalRooms} rooms allocated`
                              : ''}
                            arrow
                            disableHoverListener={!showPartial}
                          >
                            <Chip
                              icon={<HotelIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />}
                              label={showPartial ? `${allocatedRooms} / ${totalRooms}` : totalRooms}
                              size="small"
                              sx={{
                                backgroundColor: showPartial
                                  ? (isDark ? 'rgba(255, 152, 0, 0.18)' : 'rgba(255, 152, 0, 0.12)')
                                  : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                                color: showPartial ? '#ff9800' : CUSTOM_BORDER_COLOR,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                                border: `1px solid ${showPartial ? 'rgba(255, 152, 0, 0.45)' : `${CUSTOM_BORDER_COLOR}40`}`,
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </Box>
                  </Grid>
                  {String(booking.special_requests || booking.specialRequests || '').trim() && (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.25,
                          color: isDark ? '#9ca3af' : '#666',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          textTransform: 'uppercase',
                          display: 'block',
                        }}
                      >
                        Special requests
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.25,
                          px: 0.85,
                          py: 0.6,
                          borderRadius: 0.85,
                          borderLeft: '2px solid #f59e0b',
                          backgroundColor: isDark
                            ? 'rgba(250, 204, 21, 0.22)'
                            : 'rgba(254, 240, 138, 0.55)',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDark ? drawerTheme.text : '#333',
                            fontSize: '0.76rem',
                            lineHeight: 1.4,
                            whiteSpace: 'pre-line',
                            fontStyle: 'italic',
                          }}
                        >
                          {String(booking.special_requests || booking.specialRequests || '').trim()}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Check if booking is checked out */}
              {booking.status === 'checked_out' && (
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mb: 3,
                    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    This booking is already checked out. Room changes are not allowed.
                  </Typography>
                </Alert>
              )}

              {isCancelledStatus && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Room allocation is hidden for cancelled/no-show bookings. Use Modify booking to update details.
                  </Typography>
                </Alert>
              )}

              {isReserved && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    This booking is reserved. Confirm it from Modify Booking, then continue with check-in.
                  </Typography>
                </Alert>
              )}

              {/* Available Rooms by Type */}
              {!isCancelledStatus && roomTypeGroupsToRender.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: isDark ? drawerTheme.text : '#333', mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
                    {isReserved ? 'Booked rooms' : isCheckedOut ? 'Room Information' : 'Select Rooms'}
                  </Typography>
                  {roomTypeGroupsToRender.map((roomTypeGroup) => {
                      const roomTypeId = roomTypeGroup.room_type.id;
                      
                      // Calculate total booked rooms for this room type (allocated + newly selected)
                      // For partial check-in: count allocated rooms + newly selected rooms for this type
                      // For confirmed: count selected rooms for this type
                      // For checked-in: count allocated rooms for this type (divided by nights)
                      let totalBookedForType = 0;
                      const nights = booking?.nights || 1;
                      
                      // Count allocated booking items for this room type
                      const allocatedItemsForType = bookingItems.filter((item) => {
                        if (Number(item.room_type_id) === Number(roomTypeId)) {
                          const roomId = item.allocated_room_id 
                            || (item.allocated_room && (item.allocated_room.id || item.allocated_room.room_id))
                            || null;
                          return roomId !== null;
                        }
                        return false;
                      });
                      
                      // Count unique allocated rooms for this room type
                      const allocatedRoomIdsForType = new Set();
                      allocatedItemsForType.forEach((item) => {
                        const roomId = item.allocated_room_id 
                          || (item.allocated_room && (item.allocated_room.id || item.allocated_room.room_id))
                          || null;
                        if (roomId) {
                          allocatedRoomIdsForType.add(Number(roomId));
                        }
                      });
                      
                      // Calculate allocated rooms count (divided by nights)
                      // Example: 1 room allocated for 2 days = 2 booking items, so 2/2 = 1 room
                      let allocatedRoomsCount = 0;
                      if (nights > 0 && allocatedItemsForType.length > 0) {
                        allocatedRoomsCount = allocatedItemsForType.length / nights;
                        // Ensure it's a whole number (should always be, but round to be safe)
                        allocatedRoomsCount = Math.round(allocatedRoomsCount);
                      } else if (allocatedRoomIdsForType.size > 0) {
                        allocatedRoomsCount = allocatedRoomIdsForType.size;
                      }
                      
                      // In-house rooms for this type (checked_in), not merely allocated
                      const checkedInRoomIdsForType = new Set();
                      bookingItems.forEach((item) => {
                        if (Number(item.room_type_id) !== Number(roomTypeId)) return;
                        if (!isCheckedInBookingItem(item) || isCheckedOutBookingItem(item)) return;
                        const roomId = getItemAllocatedRoomId(item);
                        if (roomId != null) checkedInRoomIdsForType.add(roomId);
                      });
                      const checkedInRoomsCountForType = checkedInRoomIdsForType.size;

                      if (isContinueCheckInFlow) {
                        // Continue check-in: in-house + newly selected (not yet checked in)
                        const selectedForType = selectedRoomIds[roomTypeId] || [];
                        const newlySelectedCount = selectedForType.filter(
                          (roomId) => !checkedInRoomIdsForType.has(Number(roomId))
                        ).length;
                        totalBookedForType = checkedInRoomsCountForType + newlySelectedCount;
                      } else if (isPartialCheckout || isCheckedIn) {
                        // Fully in-house / checkout review: show checked-in count
                        totalBookedForType = checkedInRoomsCountForType || allocatedRoomsCount;
                      } else {
                        // For confirmed bookings: count selected rooms for this room type
                        totalBookedForType = selectedRoomsByType[roomTypeId] || 0;
                      }
                      
                      // Rooms needed = unique active streams for this type
                      const bookingItemsForType = bookingItems.filter(item => Number(item.room_type_id) === Number(roomTypeId));
                      const streamKeysForType = new Set();
                      bookingItemsForType.forEach((item) => {
                        if (isCheckedOutBookingItem(item)) return;
                        streamKeysForType.add(`${item.room_type_id}_${item.room_sequence || 1}_${item.allocated_room_id || item.id}`);
                      });
                      let roomsNeededForType = streamKeysForType.size;
                      if (roomsNeededForType === 0 && bookingItemsForType.length > 0) {
                        // Fallback for legacy payloads
                        if (nights > 0) {
                          roomsNeededForType = Math.round(bookingItemsForType.length / nights);
                        } else {
                          roomsNeededForType = bookingItemsForType.length;
                        }
                      }
                      if (isReserved) {
                        totalBookedForType = roomsNeededForType;
                      }
                      
                      // For checked-out bookings, if no rooms in API response, try to get from bookingItems
                      let roomsToDisplay = roomTypeGroup.rooms || [];
                      if (isCheckedOut && roomsToDisplay.length === 0 && bookingItems?.length > 0) {
                        // Extract unique allocated rooms from bookingItems for this room type
                        const allocatedRoomsFromItems = new Map();
                        bookingItems.forEach((item) => {
                          if (Number(item.room_type_id) === Number(roomTypeId) && item.allocated_room_id) {
                            if (!allocatedRoomsFromItems.has(item.allocated_room_id)) {
                              allocatedRoomsFromItems.set(item.allocated_room_id, {
                                id: item.allocated_room_id,
                                room_number: item.allocated_room_number || item.allocated_room?.room_number || `Room ${item.allocated_room_id}`,
                                status: 'occupied',
                                floor: null,
                                is_available: false,
                              });
                            }
                          }
                        });
                        roomsToDisplay = Array.from(allocatedRoomsFromItems.values());
                      }

                      // Reserved: one card per booked room stream, not every physical room of the type.
                      if (isReserved) {
                        const itemsForType = bookingItems.filter((item) => {
                          if (Number(item.room_type_id) !== Number(roomTypeId)) return false;
                          return !isCheckedOutBookingItem(item);
                        });
                        const bySeq = groupItemsBySequence(itemsForType);
                        const seqs = Object.keys(bySeq).sort((a, b) => String(a).localeCompare(String(b)));
                        const streamRooms = seqs.map((seq) => {
                          const streamItems = bySeq[seq] || [];
                          const allocatedId = streamItems
                            .map((item) => getItemAllocatedRoomId(item))
                            .find((id) => id != null);
                          if (allocatedId != null) {
                            const real = (roomTypeGroup.rooms || []).find(
                              (r) => Number(r.id) === Number(allocatedId),
                            );
                            if (real) {
                              return { ...real, reservedStreamKey: seq };
                            }
                          }
                          return {
                            id: `reserved-${roomTypeId}-${seq}`,
                            room_number: 'Unassigned',
                            status: 'reserved',
                            clean_status: null,
                            floor: null,
                            is_available: false,
                            isReservedPlaceholder: true,
                            reservedStreamKey: seq,
                          };
                        });
                        if (streamRooms.length > 0) {
                          roomsToDisplay = streamRooms;
                        } else if (roomsNeededForType > 0) {
                          roomsToDisplay = Array.from({ length: roomsNeededForType }, (_, index) => ({
                            id: `reserved-${roomTypeId}-${index + 1}`,
                            room_number: 'Unassigned',
                            status: 'reserved',
                            clean_status: null,
                            floor: null,
                            is_available: false,
                            isReservedPlaceholder: true,
                            reservedStreamKey: `seq_${index + 1}`,
                          }));
                        } else {
                          roomsToDisplay = [];
                        }
                      }

                    return (
                      <Box key={roomTypeGroup.room_type.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: isDark ? drawerTheme.text : '#333',
                              fontWeight: 600,
                              fontSize: '0.875rem',
                            }}
                          >
                            {roomTypeGroup.room_type.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                              label={`${Math.round(totalBookedForType)} / ${Math.round(roomsNeededForType)} room${Math.round(roomsNeededForType) !== 1 ? 's' : ''}`}
                              size="small"
                              sx={{
                                backgroundColor: totalBookedForType > roomsNeededForType
                                  ? (isDark ? '#e65100' : '#ef6c00')
                                  : totalBookedForType > 0
                                    ? (isDark ? '#2e7d32' : '#4caf50')
                                    : (isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5'),
                                color: totalBookedForType > 0
                                  ? '#fff'
                                  : (isDark ? drawerTheme.text : '#333'),
                                fontSize: '0.7rem',
                                fontWeight: totalBookedForType > 0 ? 600 : 400,
                              }}
                            />
                          </Box>
                        </Box>
                        <Grid container spacing={1.5}>
                          {(() => {
                            // Memoize filtered rooms for this room type
                            const filteredRooms = roomsToDisplay.filter((room) => {
                              if (isReserved) {
                                return true;
                              }
                              // If checked out, show all rooms from API (they should be allocated rooms)
                              // The backend ensures checked-out bookings only get their allocated rooms
                              if (isCheckedOut) {
                                // Show all rooms returned from API for checked-out bookings
                                // The backend filters to only return allocated rooms
                                return true;
                              }
                              // Partial checkout: only booking-allocated rooms (no disabled picker grid).
                              if (isPartialCheckout) {
                                if (allocatedRoomIds.size === 0) {
                                  return false;
                                }
                                return allocatedRoomIds.has(Number(room.id));
                              }
                              // Fully checked in with nothing pending: only allocated/in-house rooms
                              if (isCheckedIn && !isContinueCheckInFlow) {
                                if (allocatedRoomIds.size === 0) {
                                  return false;
                                }
                                return allocatedRoomIds.has(Number(room.id));
                              }
                              // Continue check-in: keep all pickable rooms visible; selection moves, rooms do not disappear.
                              if (isContinueCheckInFlow) {
                                const roomId = Number(room.id);
                                if (checkedInRoomIdsForType.has(roomId)) return true;
                                const isAllocatedForType =
                                  allocatedRoomIdsForType.has(roomId)
                                  || bookingItems.some((item) => {
                                    if (Number(item.room_type_id) !== Number(roomTypeId)) {
                                      return false;
                                    }
                                    return getItemAllocatedRoomId(item) === roomId;
                                  });
                                if (isAllocatedForType) return true;
                                if (getCheckInRoomHoldReason(room)) return true;
                                if (room.is_maintenance_blocked) return false;
                                return room.is_available !== false;
                              }
                              // For confirmed bookings, show all rooms for the room type
                              // Rooms with is_available === false will be disabled but still visible
                              return true;
                            });
                            
                            // Stable order: checked-in first (full cards), then room number.
                            // Do not reorder when the user selects/deselects a room.
                            const sortedRooms = [...filteredRooms].sort((a, b) => {
                              const aCheckedIn = checkedInRoomIdsForType.has(Number(a.id));
                              const bCheckedIn = checkedInRoomIdsForType.has(Number(b.id));
                              if (aCheckedIn !== bCheckedIn) return aCheckedIn ? -1 : 1;
                              return String(a.room_number || '').localeCompare(
                                String(b.room_number || ''),
                                undefined,
                                { numeric: true, sensitivity: 'base' },
                              );
                            });
                            
                            return sortedRooms.map((room) => {
                            // Check if this room is selected (using selectedRoomIds for faster lookup)
                            const currentSelected = selectedRoomIds[roomTypeId] || [];
                            // Number-coerce so auto-allocated ids still show the selected (direct) indicator
                            const isRoomSelected = currentSelected.some(
                              (id) => Number(id) === Number(room.id)
                            );
                            
                            // Allocated to this booking (may still be only "Booked", not checked in)
                            const isRoomAllocated = bookingItems.some((item) => {
                              const allocatedId = getItemAllocatedRoomId(item);
                              return allocatedId != null && allocatedId === Number(room.id);
                            }) || allocatedRoomIds.has(Number(room.id));

                            // Check if any booking items for this room are checked out
                            const itemsForThisRoom = bookingItems.filter((item) => {
                              const itemRoomId = getItemAllocatedRoomId(item);
                              return itemRoomId != null && itemRoomId === Number(room.id);
                            });
                            const isRoomCheckedOut = itemsForThisRoom.length > 0 && itemsForThisRoom.every((item) => isCheckedOutBookingItem(item));
                            const isRoomCheckedIn = itemsForThisRoom.some((item) => (
                              isCheckedInBookingItem(item) && !isCheckedOutBookingItem(item)
                            ));
                            const roomIsDirty = isRoomDirty(room);
                            const hasSomeItemsCheckedOut = itemsForThisRoom.some((item) => isCheckedOutBookingItem(item));
                            const hasRoomChangedInSequence = itemsForThisRoom.some((item) =>
                              changedSequenceKeys.has(`${item.room_type_id}_${item.room_sequence || 1}`)
                            );
                            const hasRoomTypeUpgradedInSequence = itemsForThisRoom.some((item) => {
                              const rtId = Number((item.room_type_id ?? item.room_type?.id) || 0);
                              const seq = item.room_sequence || 1;
                              return rtId && upgradedRoomTypeKeys.has(`${seq}_${rtId}`);
                            });
                            const roomTypeChangeKind = hasRoomTypeUpgradedInSequence
                              ? resolveRoomTypeChangeKindFromItems(
                                itemsForThisRoom,
                                roomTypeSortOrderById,
                                bookingItems,
                              )
                              : 'upgrade';
                            const roomTypeChangeLabel = getRoomTypeChangeLabel(roomTypeChangeKind);
                            const roomTypeChangeChipSx = roomTypeChangeKind === 'downgrade'
                              ? {
                                backgroundColor: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.12)',
                                color: '#f59e0b',
                                border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.45)' : 'rgba(245, 158, 11, 0.35)'}`,
                              }
                              : {
                                backgroundColor: isDark ? 'rgba(184, 134, 11, 0.2)' : 'rgba(184, 134, 11, 0.12)',
                                color: '#b8860b',
                                border: `1px solid ${isDark ? 'rgba(184, 134, 11, 0.45)' : 'rgba(184, 134, 11, 0.35)'}`,
                              };
                            
                            // Design:
                            // - Confirmed: compact selection cards
                            // - Partial / continue check-in: full layout only for rooms already checked_in;
                            //   allocated-but-booked rooms stay compact + selectable
                            // - Fully checked-in (nothing pending): full layout
                            let useCompactDesign = false;
                            if (isCheckedOut || isRoomCheckedOut) {
                              useCompactDesign = false;
                            } else if (isConfirmed) {
                              useCompactDesign = true;
                            } else if (isContinueCheckInFlow) {
                              // Already in-house: full card; still-booked allocated rooms: selectable compact
                              useCompactDesign = !isRoomCheckedIn;
                            } else if (isPartialCheckout || isCheckedIn) {
                              useCompactDesign = false;
                            }
                            
                            let canSelectMore = false;
                            const selectedForType = selectedRoomIds[roomTypeId] || [];
                            const selectedCountForType = selectedForType.filter((id) => id != null).length;
                            
                            if (isPartialCheckout) {
                              if (isRoomCheckedOut) {
                                canSelectMore = false;
                              } else if (isRoomAllocated) {
                                canSelectMore = true;
                              } else {
                                const allocatedForType = allocatedRoomIdsForType.size;
                                const roomsNeededForThisType = roomsNeededForType;
                                const remainingForType = Math.max(0, roomsNeededForThisType - allocatedForType);
                                const newlySelectedForType = selectedForType.filter((roomId) =>
                                  !allocatedRoomIdsForType.has(Number(roomId))
                                ).length;
                                canSelectMore = newlySelectedForType < remainingForType;
                              }
                            } else if (isContinueCheckInFlow) {
                              if (isRoomCheckedIn) {
                                // Already in-house: keep selected, not newly selectable
                                canSelectMore = false;
                              } else {
                                const roomsNeededForThisType = roomsNeededForType;
                                const remainingForType = Math.max(
                                  0,
                                  roomsNeededForThisType - checkedInRoomIdsForType.size,
                                );
                                const newlySelectedForType = selectedForType.filter(
                                  (roomId) => !checkedInRoomIdsForType.has(Number(roomId))
                                ).length;
                                canSelectMore = newlySelectedForType < remainingForType;
                              }
                            } else {
                              canSelectMore = selectedCountForType < roomsNeededForType;
                            }
                            const holdReason = getCheckInRoomHoldReason(room);
                            const holdLabel = getCheckInRoomHoldLabel(holdReason);
                            const holdTooltip = getCheckInRoomHoldTooltip(holdReason, room.room_number);
                            const isHeldByOtherStay = Boolean(holdReason)
                              && !isRoomCheckedIn
                              && !(isRoomAllocated && isStayWindowHoldReason(holdReason));
                            const isRoomUnavailable = room.is_available === false || isHeldByOtherStay;
                            // At capacity: allow clicking another room to replace a non-checked-in selection
                            // (guest stream moves with the replacement). Checked-in rooms stay locked.
                            const hasReplaceableSelection = selectedForType.some(
                              (id) => !checkedInRoomIdsForType.has(Number(id)),
                            );
                            const isDisabled = isReserved
                              || isCheckedOut
                              || isRoomCheckedOut
                              || isHeldByOtherStay
                              || (isRoomUnavailable && !isRoomAllocated && !isRoomSelected)
                              || (!isRoomSelected && !canSelectMore && !isRoomCheckedIn && !hasReplaceableSelection);
                            const isSelectionLocked = isDisabled || isRoomCheckedIn;

                            // Find guests for this room — prefer current selection mapping, then allocation.
                            const itemsForRoom = bookingItems.filter((item) => {
                              if (isReserved && room.reservedStreamKey != null) {
                                if (Number(item.room_type_id) !== Number(roomTypeId)) return false;
                                if (isCheckedOutBookingItem(item)) return false;
                                const streamKey = String(room.reservedStreamKey);
                                const seq = Number(item?.room_sequence || 1);
                                return streamKey === `item_${item.id}` || streamKey === `seq_${seq}`;
                              }
                              const selectedId = selectedRooms[item.id];
                              const itemRoomId = selectedId != null && selectedId !== ''
                                ? selectedId
                                : getItemAllocatedRoomId(item);
                              return itemRoomId != null && Number(itemRoomId) === Number(room.id);
                            });
                            
                            // Get all unique guests for this room - use Map for better performance
                            const roomGuests = [];
                            const guestMap = new Map();
                            itemsForRoom.forEach((item) => {
                              const itemGuests = item.guests && item.guests.length > 0 ? item.guests : [];
                              
                              itemGuests.forEach(guest => {
                                const guestKey = guest.id || `${guest.first_name}-${guest.last_name}-${guest.email}`;
                                if (!guestMap.has(guestKey)) {
                                  guestMap.set(guestKey, true);
                                  roomGuests.push(guest);
                                }
                              });
                            });
                            const primaryGuestId = booking?.guest_id ?? booking?.guest?.id ?? null;
                            const compactRoomGuests = roomGuests.filter((guest) => {
                              if (primaryGuestId == null || guest?.id == null) return true;
                              return Number(guest.id) !== Number(primaryGuestId);
                            });
                            const firstCapacityItem = itemsForRoom?.[0];
                            const bookedAdultsForCap = (() => {
                              const fromItems = Math.max(
                                0,
                                ...(itemsForRoom || []).map((item) => Number(item?.adults) || 0),
                              );
                              if (fromItems > 0) return fromItems;
                              return Number(firstCapacityItem?.adults) || Number(booking?.pax_adults) || 1;
                            })();
                            const bookedChildrenForCap = (() => {
                              const fromItems = Math.max(
                                0,
                                ...(itemsForRoom || []).map((item) => Number(item?.children) || 0),
                              );
                              if (fromItems > 0) return fromItems;
                              return Number(firstCapacityItem?.children) || Number(booking?.pax_children) || 0;
                            })();
                            const maxGuestsForRoom = Math.max(
                              1,
                              Number(firstCapacityItem?.room_type?.max_guests)
                                || Math.max(
                                  0,
                                  ...(itemsForRoom || []).map((item) => Number(item?.room_type?.max_guests) || 0),
                                )
                                || (Number(firstCapacityItem?.room_type?.base_adults) || 0)
                                  + (Number(firstCapacityItem?.room_type?.base_children) || 0)
                                || bookedAdultsForCap + bookedChildrenForCap
                                || 4,
                            );
                            const hasPendingGuest = Boolean(
                              pendingRoomGuests[room.id] || pendingRoomGuests[String(room.id)],
                            );
                            // Pending compact guest is UI-only until check-in; count it when no persisted guest yet.
                            const stagedGuestCount = hasPendingGuest
                              ? Math.max(roomGuests.length, 1)
                              : roomGuests.length;
                            const atGuestCapacity = stagedGuestCount >= maxGuestsForRoom;
                            const pendingGuestForRoom =
                              pendingRoomGuests[room.id] ?? pendingRoomGuests[String(room.id)] ?? null;
                            const pendingGuestDisplayName = pendingGuestForRoom
                              ? (String(pendingGuestForRoom.name || '').trim()
                                || [pendingGuestForRoom.first_name, pendingGuestForRoom.middle_name, pendingGuestForRoom.last_name]
                                  .filter(Boolean)
                                  .join(' ')
                                || 'Guest')
                              : '';
                            const compactGuestLabel = compactRoomGuests.length > 0
                              ? (
                                [
                                  compactRoomGuests[0].first_name,
                                  compactRoomGuests[0].last_name,
                                ].filter(Boolean).join(' ').trim()
                                || compactRoomGuests[0].name
                                || 'Guest'
                              ) + (compactRoomGuests.length > 1 ? ` +${compactRoomGuests.length - 1}` : '')
                              : pendingGuestDisplayName;
                            const hasCompactGuest = compactRoomGuests.length > 0 || Boolean(pendingGuestForRoom);
                            const openCompactAddGuest = (editExisting = false) => {
                              const targetGuestItem =
                                pickPreferredGuestBookingItem(itemsForRoom) || itemsForRoom[0] || null;
                              editingRoomIdRef.current = room.id;
                              // Persist onto the selected room items immediately so the guest
                              // appears on the card after save (not only after Check In).
                              setDeferGuestPersist(Boolean(editExisting && pendingGuestForRoom && compactRoomGuests.length === 0));
                              setEditingRoomId(room.id);
                              setEditingGuestItemId(targetGuestItem?.id || null);
                              setEditingGuestId(null);
                              if (editExisting && pendingGuestForRoom) {
                                setInitialGuestFormData({ ...pendingGuestForRoom });
                              } else if (!editExisting && pendingGuestForRoom && compactRoomGuests.length === 0) {
                                setInitialGuestFormData({ ...pendingGuestForRoom });
                              } else {
                                setInitialGuestFormData(null);
                              }
                              setIndividualGuestDrawerOpen(true);
                            };

                            return (
                              <Grid 
                                item 
                                xs={useCompactDesign ? 6 : 12} 
                                sm={useCompactDesign ? 4 : 12} 
                                md={useCompactDesign ? 3 : 12}
                                key={room.id}
                              >
                                <Card
                                  elevation={useCompactDesign ? 1 : 3}
                                  onClick={() => {
                                    if (!isSelectionLocked && useCompactDesign && !isRoomCheckedOut) {
                                      handleRoomSelection(room, roomTypeId, isSelectionLocked);
                                    }
                                  }}
                                  sx={{
                                    backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                                    borderRadius: 3,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: useCompactDesign && !isSelectionLocked ? 'pointer' : 'default',
                                    opacity: (isCheckedOut || isRoomCheckedOut) ? 0.7 : (isDisabled && !isRoomAllocated ? 0.6 : 1),
                                    border: (isCheckedOut || isRoomCheckedOut)
                                      ? `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.2)'}`
                                      : (room.is_available === false && !isRoomAllocated
                                        ? `2px solid ${isDark ? 'rgba(255, 152, 0, 0.5)' : 'rgba(245, 124, 0, 0.5)'}`
                                        : (roomIsDirty
                                          ? `2px solid ${isDark ? 'rgba(251, 146, 60, 0.55)' : 'rgba(245, 124, 0, 0.55)'}`
                                          : 'none')),
                                    '&:hover': useCompactDesign && !isSelectionLocked ? {
                                      transform: 'translateY(-2px)',
                                      boxShadow: isDark
                                        ? '0 8px 16px rgba(76, 225, 182, 0.3)'
                                        : '0 8px 16px rgba(76, 225, 182, 0.2)',
                                    } : (isCheckedOut || isRoomCheckedOut) ? {
                                      // No hover effect for checked-out bookings or checked-out rooms
                                    } : {
                                      transform: 'translateY(-2px)',
                                      boxShadow: isDark
                                        ? '0 12px 24px rgba(0,0,0,0.5)'
                                        : '0 12px 24px rgba(0,0,0,0.12)',
                                    },
                                  }}
                                >
                                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                    {useCompactDesign ? (
                                      // Compact layout for confirmed bookings (not checked out)
                                      <Box sx={{ position: 'relative', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {/* Room Type Image - Full Height with Overlay */}
                                        <Box
                                          sx={{
                                            flex: 1,
                                            minHeight: 120,
                                            position: 'relative',
                                            backgroundImage: (roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url) 
                                              ? `url(${roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url})`
                                              : `linear-gradient(135deg, ${isDark ? '#3a3a4a' : '#e8e8e8'} 0%, ${isDark ? '#2a2a3a' : '#d8d8d8'} 100%)`,
                                            backgroundColor: isDark ? '#3a3a4a' : '#e8e8e8',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            opacity: isDisabled ? 0.5 : 1,
                                          }}
                                        >
                                          {!(roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url) && (
                                            <RoomIcon sx={{ fontSize: 32, color: isDark ? drawerTheme.subtleText : '#999', opacity: 0.5 }} />
                                          )}
                                          
                                          {/* Gradient Fade Overlay at Top for Readability */}
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              right: 0,
                                              height: '50%',
                                              background: `linear-gradient(180deg, 
                                                ${isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.6)'} 0%, 
                                                ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.4)'} 50%, 
                                                transparent 100%)`,
                                              pointerEvents: 'none',
                                            }}
                                          />
                                          
                                          {/* Checkbox and Room Number Overlay - Top Left */}
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              top: 8,
                                              left: 8,
                                              right: 8,
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              zIndex: 2,
                                            }}
                                          >
                                          <Checkbox
                                            checked={isRoomSelected || isRoomCheckedIn}
                                            disabled={isSelectionLocked}
                                            size="small"
                                            sx={{
                                                color: '#fff',
                                                backgroundColor: (isRoomSelected || isRoomCheckedIn)
                                                  ? 'rgba(76, 225, 182, 0.9)' 
                                                  : 'rgba(0, 0, 0, 0.4)',
                                                borderRadius: '4px',
                                                padding: '2px',
                                                backdropFilter: 'blur(8px)',
                                              '&.Mui-checked': {
                                                  color: '#000',
                                                  backgroundColor: 'rgba(76, 225, 182, 0.9)',
                                                },
                                                '&:hover': {
                                                  backgroundColor: (isRoomSelected || isRoomCheckedIn)
                                                    ? 'rgba(76, 225, 182, 1)' 
                                                    : 'rgba(0, 0, 0, 0.6)',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                  fontSize: 20,
                                                  filter: (isRoomSelected || isRoomCheckedIn) ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
                                                },
                                                transition: 'all 0.3s ease',
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (!isSelectionLocked) {
                                                handleRoomSelection(room, roomTypeId, isSelectionLocked);
                                              }
                                            }}
                                          />
                                            <Chip
                                              icon={<RoomIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                              label={room.room_number}
                                              size="small"
                                            sx={{
                                                backgroundColor: isRoomSelected 
                                                  ? 'rgba(76, 225, 182, 0.9)' 
                                                  : 'rgba(0, 0, 0, 0.5)',
                                                color: '#fff',
                                                fontWeight: 700,
                                              fontSize: '0.85rem',
                                                height: 28,
                                                backdropFilter: 'blur(10px)',
                                                border: `1.5px solid ${isRoomSelected ? 'rgba(76, 225, 182, 1)' : 'rgba(255, 255, 255, 0.3)'}`,
                                                boxShadow: isRoomSelected 
                                                  ? '0 4px 12px rgba(76, 225, 182, 0.4), 0 2px 4px rgba(0,0,0,0.3)' 
                                                  : '0 2px 8px rgba(0,0,0,0.4)',
                                                letterSpacing: '0.5px',
                                                transition: 'all 0.3s ease',
                                                '& .MuiChip-icon': {
                                                  color: '#fff !important',
                                                  marginLeft: '8px',
                                                },
                                                '&:hover': {
                                                  backgroundColor: isRoomSelected 
                                                    ? 'rgba(76, 225, 182, 1)' 
                                                    : 'rgba(0, 0, 0, 0.7)',
                                                  transform: 'translateY(-1px)',
                                                  boxShadow: isRoomSelected 
                                                    ? '0 6px 16px rgba(76, 225, 182, 0.5), 0 2px 4px rgba(0,0,0,0.3)' 
                                                    : '0 4px 12px rgba(0,0,0,0.5)',
                                                },
                                              }}
                                            />
                                          </Box>

                                          {isHeldByOtherStay ? (
                                            <Tooltip title={holdTooltip} arrow>
                                              <Chip
                                                label={holdLabel}
                                                size="small"
                                                sx={getCheckInRoomHoldOverlaySx(
                                                  getCheckInRoomHoldChipColor(holdReason),
                                                  isDark,
                                                )}
                                              />
                                            </Tooltip>
                                          ) : room.is_maintenance_blocked ? (
                                            <Chip
                                              label="Under Maintenance"
                                              size="small"
                                              sx={getCheckInRoomHoldOverlaySx('rgba(239, 83, 80, 0.92)', isDark)}
                                            />
                                          ) : roomIsDirty ? (
                                            <Tooltip title="Mark this room clean before check-in" arrow>
                                              <Chip
                                                label="Clean Now"
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (!isDisabled) {
                                                    enqueueDirtyRoomCleaning(room.id, roomTypeId);
                                                  }
                                                }}
                                                sx={{
                                                  ...getCheckInRoomHoldOverlaySx(
                                                    isDark ? 'rgba(251, 146, 60, 0.88)' : 'rgba(255, 152, 0, 0.9)',
                                                    isDark,
                                                  ),
                                                  cursor: isDisabled ? 'default' : 'pointer',
                                                }}
                                              />
                                            </Tooltip>
                                          ) : null}
                                        </Box>
                                        
                                        {/* Bottom Section - Room Type Name */}
                                        <Box 
                                          sx={{ 
                                            p: 1.25,
                                            backgroundColor: isDark ? 'rgba(42, 42, 49, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                            backdropFilter: 'blur(10px)',
                                            borderTop: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                                          }}
                                        >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                              color: isDark ? drawerTheme.text : '#333',
                                            fontSize: '0.75rem',
                                              fontWeight: 500,
                                            display: 'block',
                                              textAlign: 'center',
                                          }}
                                        >
                                          {roomTypeGroup.room_type.name}
                                        </Typography>
                                          {/* Compact cards: only checkout / room-change chips (no Booked chip) */}
                                          {isRoomCheckedOut ? (
                                            <Chip
                                              label="Checked Out"
                                              size="small"
                                              sx={{
                                                backgroundColor: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                                                color: '#4caf50',
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 20,
                                                border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)'}`,
                                              }}
                                            />
                                          ) : null}
                                          {hasRoomChangedInSequence && (
                                            <Chip
                                              label="Room Change"
                                              size="small"
                                              sx={{
                                                backgroundColor: isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.12)',
                                                color: '#ff9800',
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 20,
                                                border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.45)' : 'rgba(255, 152, 0, 0.35)'}`,
                                              }}
                                            />
                                          )}
                                          {hasRoomTypeUpgradedInSequence && (
                                            <Chip
                                              label={roomTypeChangeLabel}
                                              size="small"
                                              sx={{
                                                ...roomTypeChangeChipSx,
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 20,
                                              }}
                                            />
                                          )}
                                        </Box>
                                        {/* Room guests: persisted item guests, else staged form, else Add Guest. */}
                                        {(isRoomSelected || isRoomCheckedIn) && !isRoomCheckedOut && (
                                          hasCompactGuest ? (
                                            <Box
                                              onClick={(e) => e.stopPropagation()}
                                              sx={{
                                                mt: 1,
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                              }}
                                            >
                                            <Box
                                              sx={{
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                px: 0.75,
                                                py: 0.35,
                                                borderRadius: 1.5,
                                                border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                                                backgroundColor: isDark
                                                  ? 'rgba(76, 225, 182, 0.08)'
                                                  : 'rgba(76, 225, 182, 0.06)',
                                              }}
                                            >
                                              <PersonIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR, flexShrink: 0 }} />
                                              <Typography
                                                noWrap
                                                sx={{
                                                  flex: 1,
                                                  minWidth: 0,
                                                  fontSize: '0.68rem',
                                                  fontWeight: 700,
                                                  color: isDark ? drawerTheme.text : '#212121',
                                                }}
                                              >
                                                {compactGuestLabel}
                                              </Typography>
                                              {pendingGuestForRoom && compactRoomGuests.length === 0 ? (
                                              <IconButton
                                                size="small"
                                                aria-label="Edit guest"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openCompactAddGuest(true);
                                                }}
                                                sx={{
                                                  width: 24,
                                                  height: 24,
                                                  color: CUSTOM_BORDER_COLOR,
                                                  p: 0.25,
                                                }}
                                              >
                                                <EditIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                              ) : null}
                                            </Box>
                                            {!atGuestCapacity ? (
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              startIcon={<AddIcon sx={{ fontSize: '12px !important' }} />}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openCompactAddGuest(false);
                                              }}
                                              sx={{
                                                width: '100%',
                                                borderColor: CUSTOM_BORDER_COLOR,
                                                color: CUSTOM_BORDER_COLOR,
                                                fontSize: '0.6rem',
                                                fontWeight: 700,
                                                height: 24,
                                                minHeight: 24,
                                                maxHeight: 24,
                                                lineHeight: 1,
                                                borderRadius: 1.5,
                                                textTransform: 'none',
                                                whiteSpace: 'nowrap',
                                                px: 0.5,
                                                '& .MuiButton-startIcon': {
                                                  mr: 0.4,
                                                  ml: 0,
                                                },
                                                '& .MuiButton-startIcon > *:nth-of-type(1)': {
                                                  fontSize: 12,
                                                },
                                                '&:hover': {
                                                  borderColor: CUSTOM_BORDER_COLOR,
                                                  backgroundColor: isDark
                                                    ? 'rgba(76, 225, 182, 0.12)'
                                                    : 'rgba(76, 225, 182, 0.08)',
                                                },
                                              }}
                                            >
                                              Add Guest
                                            </Button>
                                            ) : null}
                                            </Box>
                                          ) : (
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (atGuestCapacity) return;
                                                openCompactAddGuest(false);
                                              }}
                                              disabled={atGuestCapacity}
                                              title={
                                                atGuestCapacity
                                                  ? `Maximum ${maxGuestsForRoom} guest${maxGuestsForRoom === 1 ? '' : 's'} for this room type`
                                                  : undefined
                                              }
                                              sx={{
                                                mt: 1,
                                                width: '100%',
                                                borderColor: CUSTOM_BORDER_COLOR,
                                                color: CUSTOM_BORDER_COLOR,
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                height: 28,
                                                minHeight: 28,
                                                borderRadius: 1.5,
                                                textTransform: 'none',
                                                px: 0.75,
                                                '&:hover': {
                                                  borderColor: CUSTOM_BORDER_COLOR,
                                                  backgroundColor: isDark
                                                    ? 'rgba(76, 225, 182, 0.12)'
                                                    : 'rgba(76, 225, 182, 0.08)',
                                                },
                                                '&.Mui-disabled': {
                                                  borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                                                  color: isDark ? drawerTheme.subtleText : '#999',
                                                },
                                              }}
                                            >
                                              Add Guest
                                            </Button>
                                          )
                                        )}
                                        </Box>
                                      </Box>
                                    ) : (
                                      // Full layout for checked-in or checked-out bookings
                                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', minHeight: 100, minWidth: { xs: 0 } }}>
                                        {/* Room Type Image - Left Side with Gradient Overlay */}
                                        <Box
                                          sx={{
                                            ...bookingDrawerRoomImageSizeSx,
                                            position: 'relative',
                                            backgroundImage: (roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url) 
                                              ? `linear-gradient(135deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 100%), url(${roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url})`
                                              : `linear-gradient(135deg, ${isDark ? '#3a3a4a' : '#e8e8e8'} 0%, ${isDark ? '#2a2a3a' : '#d8d8d8'} 100%)`,
                                            backgroundColor: isDark ? '#3a3a4a' : '#e8e8e8',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            borderTopLeftRadius: 12,
                                            borderBottomLeftRadius: 12,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                          }}
                                        >
                                          {!(roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url) ? (
                                            <RoomIcon sx={{ fontSize: 40, color: isDark ? drawerTheme.subtleText : '#999', opacity: 0.5 }} />
                                          ) : null}
                                          
                                          {/* Room Number Badge on Image */}
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              bottom: 8,
                                              left: 8,
                                              right: 8,
                                              zIndex: 1,
                                            }}
                                          >
                                            <Chip
                                              icon={<RoomIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                              label={room.room_number}
                                              size="small"
                                              sx={{
                                                backgroundColor: 'rgba(0,0,0,0.75)',
                                                color: '#fff',
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                height: 24,
                                                backdropFilter: 'blur(10px)',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                                '& .MuiChip-icon': {
                                                  color: '#fff !important',
                                                  fontSize: 14,
                                                },
                                              }}
                                            />
                                          </Box>
                                        </Box>
                                        
                                        {/* Guest Information - Right Side */}
                                        <Box sx={{ 
                                          flex: 1,
                                          minWidth: { xs: 0 },
                                          p: 1.5, 
                                          display: 'flex', 
                                          flexDirection: 'column', 
                                          justifyContent: 'space-between',
                                          background: isDark 
                                            ? drawerTheme.surfaceGradient
                                            : 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                                        }}>
                                        {/* Room Type, Rate Plan, and Pricing Information */}
                                        {(() => {
                                          // Get booking items for this room to calculate pricing
                                          const firstItem = itemsForRoom.length > 0 ? itemsForRoom[0] : null;
                                          const ratePlanName = firstItem?.rate_plan?.name || null;
                                          const roomDateKeys = new Set(
                                            (itemsForRoom || [])
                                              .map((item) => {
                                                if (!item?.date) return '';
                                                return moment.isMoment(item.date)
                                                  ? item.date.format('YYYY-MM-DD')
                                                  : String(item.date).slice(0, 10);
                                              })
                                              .filter(Boolean)
                                          );
                                          const nights = roomDateKeys.size || itemsForRoom.length || 0;
                                          const totalPrice = (itemsForRoom || []).reduce((sum, item) => {
                                            const amount = Number(item?.price);
                                            return sum + (Number.isFinite(amount) ? amount : 0);
                                          }, 0);
                                          const pricePerNight = nights > 0 ? totalPrice / nights : 0;
                                          const itemAdults = Math.max(
                                            0,
                                            ...(itemsForRoom || []).map((item) => Number(item?.adults) || 0),
                                          );
                                          const itemChildren = Math.max(
                                            0,
                                            ...(itemsForRoom || []).map((item) => Number(item?.children) || 0),
                                          );
                                          const bookedAdults = itemAdults > 0
                                            ? itemAdults
                                            : (Number(firstItem?.adults) || Number(booking?.pax_adults) || 0);
                                          const roomChildren = itemChildren > 0
                                            ? itemChildren
                                            : (Number(firstItem?.children) || Number(booking?.pax_children) || 0);
                                          // Show booked adults; backend only raises adults when named guests exceed that.
                                          const roomAdults = bookedAdults;
                                          
                                          return (
                                            <Box sx={{ mb: 1.5 }}>
                                              {/* Room Type with Rate Plan */}
                                              <Box sx={{ mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', width: '100%', justifyContent: 'space-between' }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: { xs: 0 }, flex: { xs: '1 1 150px', sm: 1 } }}>
                                                  <HotelIcon sx={{ fontSize: 16, color: CUSTOM_BORDER_COLOR, flexShrink: 0 }} />
                                                  <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                      color: isDark ? '#fff' : '#1a1a1a',
                                                      fontWeight: 700,
                                                      fontSize: '0.85rem',
                                                      letterSpacing: '0.01em',
                                                      lineHeight: 1.2,
                                                      minWidth: { xs: 0 },
                                                      maxWidth: { xs: '100%', sm: 'none' },
                                                    }}
                                                  >
                                                    {roomTypeGroup.room_type.name}
                                                  </Typography>
                                                  {ratePlanName && (
                                                    <>
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          color: isDark ? drawerTheme.subtleText : '#666',
                                                          fontSize: '0.7rem',
                                                          fontStyle: 'italic',
                                                        }}
                                                      >
                                                        ({ratePlanName})
                                                      </Typography>
                                                    </>
                                                    )}
                                                  </Box>
                                                  {/* Stay status */}
                                                  {isRoomCheckedOut ? (
                                                    <Chip
                                                      label="Checked Out"
                                                      size="small"
                                                      sx={{
                                                        backgroundColor: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                                                        color: '#4caf50',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        height: { xs: 20, sm: 22 },
                                                        ml: { xs: 'auto', sm: 0 },
                                                        maxWidth: { xs: '100%', sm: 'none' },
                                                        border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)'}`,
                                                      }}
                                                    />
                                                  ) : isRoomCheckedIn ? (
                                                    <Chip
                                                      icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                                                      label="Checked In"
                                                      size="small"
                                                      sx={{
                                                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(0, 160, 133, 0.12)',
                                                        color: isDark ? '#4ce1b6' : '#00897b',
                                                        fontWeight: 700,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        height: { xs: 20, sm: 22 },
                                                        ml: { xs: 'auto', sm: 0 },
                                                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.45)' : 'rgba(0, 160, 133, 0.35)'}`,
                                                        '& .MuiChip-icon': { color: 'inherit' },
                                                      }}
                                                    />
                                                  ) : isRoomAllocated ? (
                                                    <Chip
                                                      label="Booked"
                                                      size="small"
                                                      sx={{
                                                        backgroundColor: isDark ? 'rgba(255, 167, 38, 0.18)' : 'rgba(255, 152, 0, 0.12)',
                                                        color: isDark ? '#ffb74d' : '#ef6c00',
                                                        fontWeight: 700,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        height: { xs: 20, sm: 22 },
                                                        ml: { xs: 'auto', sm: 0 },
                                                        border: `1px solid ${isDark ? 'rgba(255, 167, 38, 0.45)' : 'rgba(255, 152, 0, 0.35)'}`,
                                                      }}
                                                    />
                                                  ) : null}
                                                  {hasRoomChangedInSequence && (
                                                    <Chip
                                                      label="Room Change"
                                                      size="small"
                                                      sx={{
                                                        backgroundColor: isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.12)',
                                                        color: '#ff9800',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        height: { xs: 20, sm: 22 },
                                                        ml: 0.5,
                                                        border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.45)' : 'rgba(255, 152, 0, 0.35)'}`,
                                                      }}
                                                    />
                                                  )}
                                                  {hasRoomTypeUpgradedInSequence && (
                                                    <Chip
                                                      label={roomTypeChangeLabel}
                                                      size="small"
                                                      sx={{
                                                        ...roomTypeChangeChipSx,
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        height: { xs: 20, sm: 22 },
                                                        ml: 0.5,
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                              </Box>
                                              
                                              {/* Pricing Information Card */}
                                              <Paper
                                                elevation={0}
                                                sx={{
                                                  p: 1,
                                                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                                                  borderRadius: 1.5,
                                                  border: `1px solid ${CUSTOM_BORDER_COLOR}25`,
                                                }}
                                              >
                                                {/* Price Per Night + occupancy */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, minWidth: { xs: 0 }, gap: 1 }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: { xs: 0 } }}>
                                                    <Typography
                                                      component="span"
                                                      sx={{
                                                        color: CUSTOM_BORDER_COLOR,
                                                        fontSize: '1rem',
                                                        fontWeight: 800,
                                                        lineHeight: 1,
                                                      }}
                                                    >
                                                      {bookingCurrencySymbol}
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        color: isDark ? drawerTheme.text : '#333',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      {pricePerNight.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                                    </Typography>
                                                  </Box>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                                      <PersonIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          color: isDark ? drawerTheme.text : '#333',
                                                          fontSize: '0.7rem',
                                                          fontWeight: 600,
                                                          lineHeight: 1,
                                                        }}
                                                      >
                                                        {roomAdults}
                                                      </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                                      <ChildCareIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          color: isDark ? drawerTheme.text : '#333',
                                                          fontSize: '0.7rem',
                                                          fontWeight: 600,
                                                          lineHeight: 1,
                                                        }}
                                                      >
                                                        {roomChildren}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                </Box>
                                                
                                                {/* Rooms × Nights = Total */}
                                                <Box sx={{ 
                                                  display: 'flex', 
                                                  alignItems: 'center', 
                                                  justifyContent: 'space-between',
                                                  gap: { xs: 0.75, sm: 0 },
                                                  minWidth: { xs: 0 },
                                                  pt: 0.75,
                                                  borderTop: `1px solid ${CUSTOM_BORDER_COLOR}20`,
                                                }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: { xs: 0 } }}>
                                                    <HelpOutlineIcon sx={{ fontSize: 14, color: isDark ? drawerTheme.subtleText : '#666', flexShrink: 0 }} />
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        color: isDark ? drawerTheme.subtleText : '#666',
                                                        fontSize: '0.75rem',
                                                        overflow: { xs: 'hidden', sm: 'visible' },
                                                        textOverflow: { xs: 'ellipsis', sm: 'clip' },
                                                        whiteSpace: { xs: 'nowrap', sm: 'normal' },
                                                      }}
                                                    >
                                                      Room × {nights} {nights === 1 ? 'night' : 'nights'}
                                                    </Typography>
                                                  </Box>
                                                  
                                                  {/* Total Price */}
                                                  <Chip
                                                    label={`${bookingCurrencySymbol} ${totalPrice.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`}
                                                    sx={{
                                                      backgroundColor: CUSTOM_BORDER_COLOR,
                                                      color: '#000',
                                                      fontWeight: 700,
                                                      fontSize: '0.8rem',
                                                      height: 26,
                                                      flexShrink: { xs: 0, sm: 'unset' },
                                                      boxShadow: `0 2px 6px ${CUSTOM_BORDER_COLOR}40`,
                                                      '&:hover': {
                                                        boxShadow: `0 3px 8px ${CUSTOM_BORDER_COLOR}60`,
                                                      },
                                                    }}
                                                  />
                                                </Box>
                                              </Paper>
                                            </Box>
                                          );
                                        })()}
                                        
                                        {/* Guest Slider Section */}
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                          {roomGuests.length > 0 ? (
                                            <Box sx={{ position: 'relative', minHeight: 50, py: 0.5 }}>
                                              {(() => {
                                                const currentSlideIndex = guestSlideIndex[room.id] || 0;
                                                // Ensure index is within bounds
                                                const safeIndex = Math.min(Math.max(0, currentSlideIndex), roomGuests.length - 1);
                                                const currentGuest = roomGuests[safeIndex];
                                                
                                                // Safety check: ensure currentGuest exists
                                                if (!currentGuest) {
                                                  return (
                                                    <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', textAlign: 'center' }}>
                                                      No guest data available
                                                    </Typography>
                                                  );
                                                }
                                                
                                                const guestName = currentGuest.name || `${currentGuest.first_name || ''} ${currentGuest.middle_name || ''} ${currentGuest.last_name || ''}`.trim() || 'Guest';
                                                const guestPhone = currentGuest.phone || '—';
                                                
                                                return (
                                                  <Box
                                                    sx={{
                                                      position: 'relative',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      gap: 1,
                                                    }}
                                                  >
                                                    {/* Previous Button */}
                                                    {roomGuests.length > 1 && (
                                                      <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const prevIndex = safeIndex > 0 ? safeIndex - 1 : roomGuests.length - 1;
                                                          setGuestSlideIndex(prev => ({ ...prev, [room.id]: prevIndex }));
                                                        }}
                                                        sx={{
                                                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                          color: CUSTOM_BORDER_COLOR,
                                                          border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                                                          width: 32,
                                                          height: 32,
                                                          '&:hover': {
                                                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.2)',
                                                            transform: 'scale(1.1)',
                                                          },
                                                          transition: 'all 0.2s ease',
                                                        }}
                                                      >
                                                        <ChevronLeftIcon sx={{ fontSize: 20 }} />
                                                      </IconButton>
                                                    )}
                                                    
                                                    {/* Guest Info Card */}
                                                    <Paper
                                                      elevation={0}
                                                      sx={{
                                                        flex: 1,
                                                        minWidth: { xs: 0 },
                                                        p: 1,
                                                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                                                        borderRadius: 1.5,
                                                        border: `1px solid ${CUSTOM_BORDER_COLOR}30`,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                                                        },
                                                      }}
                                                    >
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {/* Avatar on Left */}
                                                        <Avatar
                                                          sx={{
                                                            width: 32,
                                                            height: 32,
                                                            bgcolor: CUSTOM_BORDER_COLOR,
                                                            color: '#000',
                                                            fontWeight: 700,
                                                            fontSize: '0.85rem',
                                                            flexShrink: 0,
                                                          }}
                                                        >
                                                          {guestName.charAt(0).toUpperCase()}
                                                        </Avatar>
                                                        
                                                        {/* Name and Phone on Right */}
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                                            <DrawerGuestFieldLink
                                                              isDark={isDark}
                                                              guest={currentGuest}
                                                              guestId={currentGuest?.id}
                                                              booking={booking}
                                                              variant="body2"
                                                              onNavigate={onClose}
                                                              sx={{
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem',
                                                                textOverflow: 'ellipsis',
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                display: 'block',
                                                                minWidth: 0,
                                                              }}
                                                            >
                                                              {guestName}
                                                            </DrawerGuestFieldLink>
                                                            {isGuestChild(currentGuest) && (
                                                              <Chip
                                                                icon={<ChildCareIcon sx={{ fontSize: '12px !important' }} />}
                                                                label="Child"
                                                                size="small"
                                                                sx={{
                                                                  height: 18,
                                                                  fontSize: '0.6rem',
                                                                  fontWeight: 700,
                                                                  flexShrink: 0,
                                                                  bgcolor: isDark ? 'rgba(33, 150, 243, 0.22)' : 'rgba(33, 150, 243, 0.12)',
                                                                  color: isDark ? '#90caf9' : '#1565c0',
                                                                  border: `1px solid ${isDark ? 'rgba(144, 202, 249, 0.45)' : 'rgba(21, 101, 192, 0.3)'}`,
                                                                  '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                                                                }}
                                                              />
                                                            )}
                                                            {isForeignGuestRecord(currentGuest, hotelCountry) && (
                                                              <Chip
                                                                icon={<PublicIcon sx={{ fontSize: '12px !important' }} />}
                                                                label="Foreign"
                                                                size="small"
                                                                sx={getForeignGuestChipSx(isDark)}
                                                              />
                                                            )}
                                                          </Box>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <PhoneIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                                                            <DrawerGuestFieldLink
                                                              isDark={isDark}
                                                              guest={currentGuest}
                                                              guestId={currentGuest?.id}
                                                              booking={booking}
                                                              variant="caption"
                                                              onNavigate={onClose}
                                                              sx={{
                                                                fontSize: '0.7rem',
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              {guestPhone}
                                                            </DrawerGuestFieldLink>
                                                          </Box>
                                                          {roomGuests.length > 1 && (
                                                            <Chip
                                                              label={`${safeIndex + 1} / ${roomGuests.length}`}
                                                              size="small"
                                                              sx={{
                                                                mt: 0.5,
                                                                height: 18,
                                                                fontSize: '0.6rem',
                                                                bgcolor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                                                                color: CUSTOM_BORDER_COLOR,
                                                                fontWeight: 600,
                                                              }}
                                                            />
                                                          )}
                                                        </Box>
                                                        
                                                        {/* Action Icons */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                                          {/* Edit Guest Icon */}
                                                          <IconButton
                                                            size="small"
                                                            onClick={async (e) => {
                                                              e.stopPropagation();
                                                              // Find the booking item that has this guest
                                                              const itemWithGuest = itemsForRoom.find(item => {
                                                                const itemGuests = item.guests && item.guests.length > 0 ? item.guests : [];
                                                                return itemGuests.some(g => 
                                                                  (g.id && currentGuest.id && g.id === currentGuest.id) ||
                                                                  (g.first_name === currentGuest.first_name && g.last_name === currentGuest.last_name && g.email === currentGuest.email)
                                                                );
                                                              });
                                                              
                                                              setEditingRoomId(room.id);
                                                              setEditingGuestItemId(itemWithGuest?.id || null);
                                                              setEditingGuestId(currentGuest.id || null);
                                                              
                                                              // Build initialGuest prefill for BookingGuestFormDrawer
                                                              const fullName = [
                                                                currentGuest.first_name || '',
                                                                currentGuest.middle_name || '',
                                                                currentGuest.last_name || ''
                                                              ].filter(Boolean).join(' ');
                                                              
                                                              let phoneNumber = '';
                                                              let countryCode = '+91';
                                                              if (currentGuest.phone) {
                                                                const phoneStr = currentGuest.phone.toString();
                                                                if (phoneStr.includes('-')) {
                                                                  const parts = phoneStr.split('-');
                                                                  if (parts.length >= 2) {
                                                                    countryCode = parts[0].startsWith('+') ? parts[0] : '+' + parts[0];
                                                                    phoneNumber = parts.slice(1).join('').replace(/\D/g, '');
                                                                  } else {
                                                                    phoneNumber = phoneStr.replace(/\D/g, '');
                                                                  }
                                                                } else if (phoneStr.startsWith('+')) {
                                                                  const m = phoneStr.match(/^(\+\d{1,3})(\d+)$/);
                                                                  if (m) { countryCode = m[1]; phoneNumber = m[2]; }
                                                                  else phoneNumber = phoneStr.replace(/\D/g, '');
                                                                } else {
                                                                  phoneNumber = phoneStr.replace(/\D/g, '');
                                                                }
                                                              }
                                                              if (currentGuest.country_code) {
                                                                countryCode = currentGuest.country_code.startsWith('+') ? currentGuest.country_code : '+' + currentGuest.country_code;
                                                              }
                                                              let countryDisplay = currentGuest.country || '';
                                                              if (currentGuest.country) {
                                                                const countryObj = countriesData.countries.find(c => c.code === currentGuest.country || c.name === currentGuest.country);
                                                                countryDisplay = countryObj ? countryObj.name : currentGuest.country;
                                                              }
                                                              setInitialGuestFormData({
                                                                title: currentGuest.title || '',
                                                                name: fullName,
                                                                first_name: currentGuest.first_name || '',
                                                                middle_name: currentGuest.middle_name || '',
                                                                last_name: currentGuest.last_name || '',
                                                                email: currentGuest.email || '',
                                                                phone: phoneNumber,
                                                                phone_country_code: countryCode,
                                                                dob: currentGuest.dob ? String(currentGuest.dob).slice(0, 10) : '',
                                                                gender: currentGuest.gender || '',
                                                                address: currentGuest.address_line1 || '',
                                                                city: currentGuest.city || '',
                                                                state: currentGuest.state || '',
                                                                country: countryDisplay || resolveHotelCountryName(),
                                                                zipcode: currentGuest.pincode || '',
                                                                camera_upload: false,
                                                                do_not_rent: false,
                                                                do_not_rent_severity: 'HIGH',
                                                                do_not_rent_reason: '',
                                                                id_front: '',
                                                                id_back: '',
                                                                document_type: currentGuest.document_type || NO_DOCUMENT_VALUE,
                                                                id_number: currentGuest.id_number || '',
                                                                // Flags for BookingGuestFormDrawer to auto-fetch signed image URLs
                                                                has_id_front: Boolean(currentGuest.has_id_front),
                                                                has_id_back: Boolean(currentGuest.has_id_back),
                                                              });
                                                              // Prevent opening drawer for confirmed bookings
                                                              const bookingStatus = String(booking?.status || '').toLowerCase();
                                                              if (bookingStatus !== 'confirmed') {
                                                                setIndividualGuestDrawerOpen(true);
                                                              }
                                                            }}
                                                            sx={{
                                                              color: isCheckedOut ? (isDark ? drawerTheme.subtleText : '#999') : CUSTOM_BORDER_COLOR,
                                                              opacity: isCheckedOut ? 0.5 : 1,
                                                              '&:hover': {
                                                                backgroundColor: isCheckedOut ? 'transparent' : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                                                              },
                                                              width: 28,
                                                              height: 28,
                                                              '&.Mui-disabled': {
                                                                color: isDark ? drawerTheme.subtleText : '#999',
                                                              },
                                                            }}
                                                          >
                                                            <EditIcon sx={{ fontSize: 16 }} />
                                                          </IconButton>
                                                          
                                                          {/* View Document Icon - Always show, check documents on click */}
                                                          <IconButton
                                                            size="small"
                                                            onClick={async (e) => {
                                                              e.stopPropagation();
                                                              
                                                              // Fetch document signed URLs on demand
                                                              try {
                                                                const docData = await getBookingGuestDocuments(bookingId, currentGuest.id);
                                                              setSelectedGuestDocuments(buildGuestDocumentsPayload({
                                                                docData,
                                                                guest: currentGuest,
                                                                booking: { ...booking, payments: paymentHistory },
                                                                guestName,
                                                                guestPhone: currentGuest.phone || booking?.guest_phone || booking?.phone,
                                                                roomNumber: room.room_number,
                                                              }));
                                                              setDocumentDrawerOpen(true);
                                                              } catch (err) {
                                                                // Still open drawer with available data
                                                                setSelectedGuestDocuments(buildGuestDocumentsPayload({
                                                                  guest: currentGuest,
                                                                  booking: { ...booking, payments: paymentHistory },
                                                                  guestName,
                                                                  guestPhone: currentGuest.phone || booking?.guest_phone || booking?.phone,
                                                                  roomNumber: room.room_number,
                                                                }));
                                                                setDocumentDrawerOpen(true);
                                                              }
                                                            }}
                                                            sx={{
                                                              color: (currentGuest.has_id_front || currentGuest.has_id_back) 
                                                                ? CUSTOM_BORDER_COLOR 
                                                                : (isDark ? drawerTheme.subtleText : '#999'),
                                                              opacity: (currentGuest.has_id_front || currentGuest.has_id_back) ? 1 : 0.5,
                                                              '&:hover': {
                                                                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                                opacity: 1,
                                                              },
                                                              width: 28,
                                                              height: 28,
                                                            }}
                                                          >
                                                            <DescriptionIcon sx={{ fontSize: 16 }} />
                                                          </IconButton>
                                                          
                                                          {/* Delete Guest Icon */}
                                                          <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              // Find the booking item that has this guest
                                                              const itemWithGuest = itemsForRoom.find(item => {
                                                                const itemGuests = item.guests && item.guests.length > 0 ? item.guests : [];
                                                                return itemGuests.some(g => 
                                                                  (g.id && currentGuest.id && g.id === currentGuest.id) ||
                                                                  (g.first_name === currentGuest.first_name && g.last_name === currentGuest.last_name && g.email === currentGuest.email)
                                                                );
                                                              });
                                                              
                                                              if (!itemWithGuest || !currentGuest.id) {
                                                                setError('Unable to find guest or booking item.');
                                                                return;
                                                              }
                                                              
                                                              // Open delete confirmation dialog
                                                              setGuestToDelete({
                                                                guest: currentGuest,
                                                                bookingItemId: itemWithGuest.id,
                                                                roomId: room.id,
                                                                guestName: guestName,
                                                              });
                                                              setDeleteGuestDialogOpen(true);
                                                            }}
                                                            sx={{
                                                              color: isCheckedOut ? (isDark ? drawerTheme.subtleText : '#999') : (isDark ? '#f44336' : '#d32f2f'),
                                                              opacity: isCheckedOut ? 0.5 : 1,
                                                              '&:hover': {
                                                                backgroundColor: isCheckedOut ? 'transparent' : (isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(211, 47, 47, 0.1)'),
                                                              },
                                                              width: 28,
                                                              height: 28,
                                                              '&.Mui-disabled': {
                                                                color: isDark ? drawerTheme.subtleText : '#999',
                                                              },
                                                            }}
                                                          >
                                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                                          </IconButton>
                                                        </Box>
                                                      </Box>
                                                    </Paper>
                                                    
                                                    {/* Next Button */}
                                                    {roomGuests.length > 1 && (
                                                      <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const nextIndex = safeIndex < roomGuests.length - 1 ? safeIndex + 1 : 0;
                                                          setGuestSlideIndex(prev => ({ ...prev, [room.id]: nextIndex }));
                                                        }}
                                                        sx={{
                                                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                                          color: CUSTOM_BORDER_COLOR,
                                                          border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                                                          width: 32,
                                                          height: 32,
                                                          '&:hover': {
                                                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.2)',
                                                            transform: 'scale(1.1)',
                                                          },
                                                          transition: 'all 0.2s ease',
                                                        }}
                                                      >
                                                        <ChevronRightIcon sx={{ fontSize: 20 }} />
                                                      </IconButton>
                                                    )}
                                                  </Box>
                                                );
                                              })()}
                                            </Box>
                                          ) : (
                                            <Box sx={{ 
                                              minHeight: 60, 
                                              display: 'flex', 
                                              flexDirection: 'column',
                                              alignItems: 'center', 
                                              justifyContent: 'center',
                                              py: 2,
                                            }}>
                                              <Avatar
                                                sx={{
                                                  width: 48,
                                                  height: 48,
                                                  mb: 1,
                                                  bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                                  color: isDark ? drawerTheme.subtleText : '#999',
                                                }}
                                              >
                                                <PersonIcon />
                                              </Avatar>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: isDark ? drawerTheme.subtleText : '#999',
                                                  fontSize: '0.75rem',
                                                  fontStyle: 'italic',
                                                }}
                                              >
                                                No guests added
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                        
                                        {/* Add Guest Button — capped by room-type max_guests */}
                                        {!isConfirmed && !isReserved && (
                                        <Button
                                          variant={roomGuests.length > 0 ? "outlined" : "contained"}
                                          size="small"
                                          startIcon={<AddIcon />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (atGuestCapacity) return;
                                            const bookingStatus = String(booking?.status || '').toLowerCase();
                                            if (bookingStatus === 'confirmed') {
                                              return;
                                            }
                                            const targetGuestItem =
                                              pickPreferredGuestBookingItem(itemsForRoom) || itemsForRoom[0];
                                            if (!targetGuestItem) {
                                              setError('No booking items found for this room.');
                                              return;
                                            }
                                            setEditingRoomId(room.id);
                                            setEditingGuestItemId(targetGuestItem.id);
                                            setEditingGuestId(null);
                                                          setInitialGuestFormData(null); // use empty defaults in BookingGuestFormDrawer
                                            setIndividualGuestDrawerOpen(true);
                                          }}
                                          disabled={
                                            atGuestCapacity
                                            || isCheckedOut
                                            || isRoomCheckedOut
                                            || (isDisabled && !isCheckedOut && !isRoomCheckedOut)
                                          }
                                          title={
                                            atGuestCapacity
                                              ? `Maximum ${maxGuestsForRoom} guest${maxGuestsForRoom === 1 ? '' : 's'} for this room type`
                                              : undefined
                                          }
                                          sx={{
                                            mt: 1.5,
                                            borderColor: CUSTOM_BORDER_COLOR,
                                            backgroundColor: roomGuests.length > 0 ? 'transparent' : CUSTOM_BORDER_COLOR,
                                            color: roomGuests.length > 0 ? CUSTOM_BORDER_COLOR : '#000',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            height: 36,
                                            width: '100%',
                                            alignSelf: 'stretch',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            boxShadow: roomGuests.length > 0 ? 'none' : `0 2px 8px ${CUSTOM_BORDER_COLOR}40`,
                                            opacity: isCheckedOut || atGuestCapacity ? 0.5 : 1,
                                            '&:hover': {
                                              borderColor: CUSTOM_BORDER_COLOR,
                                              backgroundColor: isCheckedOut || atGuestCapacity ? 'transparent' : (roomGuests.length > 0 
                                                ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)')
                                                : CUSTOM_BORDER_COLOR),
                                              color: roomGuests.length > 0 ? CUSTOM_BORDER_COLOR : '#000',
                                              transform: isCheckedOut || atGuestCapacity ? 'none' : 'translateY(-1px)',
                                              boxShadow: isCheckedOut || atGuestCapacity ? 'none' : `0 4px 12px ${CUSTOM_BORDER_COLOR}50`,
                                            },
                                            transition: 'all 0.2s ease',
                                            '&:disabled': {
                                              borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                                              backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                                              color: isDark ? drawerTheme.subtleText : '#999',
                                            },
                                          }}
                                        >
                                          Add Guest
                                        </Button>
                                        )}
                                      </Box>
                                    </Box>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })})()}
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              ) : !isCancelledStatus ? (
                (
                  <Alert severity="info">
                    No available rooms found for this booking.
                  </Alert>
                )
              ) : null}

              {(() => {
                const bookingStatusLocal = String(booking?.status || '').toLowerCase();
                const isConfirmedLocal = bookingStatusLocal === 'confirmed';
                if (!isConfirmedLocal || !hasSelectedRooms || !booking) return null;
                return (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 1.5, borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0' }} />
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: isDark ? '#e0e0e0' : '#212121',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        mb: 1,
                      }}
                    >
                      Purpose of stay
                      <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                        *
                      </Box>
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        mb: purposeOfStay === 'other' ? 1 : 0,
                      }}
                    >
                      {PURPOSE_OF_STAY_OPTIONS.map((opt) => {
                        const selected = purposeOfStay === opt.value;
                        return (
                          <Button
                            key={opt.value}
                            size="small"
                            onClick={() => {
                              setPurposeOfStay(opt.value);
                              if (opt.value !== 'other') {
                                setPurposeOfStayNote('');
                              }
                            }}
                            sx={{
                              minWidth: 0,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 999,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              border: `1px solid ${
                                selected
                                  ? CUSTOM_BORDER_COLOR
                                  : isDark
                                    ? 'rgba(255,255,255,0.2)'
                                    : 'rgba(0,0,0,0.15)'
                              }`,
                              color: selected
                                ? (isDark ? '#fff' : '#111')
                                : isDark
                                  ? 'rgba(255,255,255,0.85)'
                                  : 'rgba(0,0,0,0.75)',
                              backgroundColor: selected
                                ? isDark
                                  ? 'rgba(76, 225, 182, 0.18)'
                                  : 'rgba(76, 225, 182, 0.2)'
                                : isDark
                                  ? 'rgba(255,255,255,0.04)'
                                  : 'rgba(0,0,0,0.02)',
                              boxShadow: 'none',
                              '&:hover': {
                                borderColor: CUSTOM_BORDER_COLOR,
                                backgroundColor: selected
                                  ? isDark
                                    ? 'rgba(76, 225, 182, 0.22)'
                                    : 'rgba(76, 225, 182, 0.28)'
                                  : isDark
                                    ? 'rgba(255,255,255,0.08)'
                                    : 'rgba(0,0,0,0.04)',
                              },
                            }}
                          >
                            {opt.label}
                          </Button>
                        );
                      })}
                    </Box>
                    {purposeOfStay === 'other' ? (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Briefly describe the purpose"
                        value={purposeOfStayNote}
                        onChange={(e) => setPurposeOfStayNote(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        sx={{
                          mt: 0.5,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
                            fontSize: '0.8rem',
                          },
                        }}
                      />
                    ) : null}
                  </Box>
                );
              })()}

              {/* Payment Summary - Show for all bookings except checked out */}
              {(() => {
                const bookingStatus = String(booking?.status || '').toLowerCase();
                const isCheckedOut = bookingStatus === 'checked_out';
                const isConfirmed = bookingStatus === 'confirmed';
                
                if (!isCheckedOut && booking) {
                  return (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 1.5, borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0' }} />
                      
                      <BookingDrawerPaymentCard
                        booking={booking}
                        payments={paymentHistory}
                        isDark={isDark}
                        drawerTheme={drawerTheme}
                        accentColor={CUSTOM_BORDER_COLOR}
                        onAddPayment={() => {
                              setPaymentDrawerOpen(true);
                            }}
                        onRestaurantPaymentsChanged={async () => {
                          onBookingUpdated?.(bookingId);
                          void fetchAvailableRooms();
                        }}
                        clearMessage="All payments have been collected. You can proceed with check-in."
                      />
                    </Box>
                  );
                }
                return null;
              })()}

            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={submitting}
            sx={{
              borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
              color: isDark ? drawerTheme.text : '#666',
              '&:hover': {
                borderColor: CUSTOM_BORDER_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                color: CUSTOM_BORDER_COLOR,
              },
              '&:disabled': {
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            Cancel
          </Button>
            </Grid>
            <Grid item xs={6}>
              {(() => {
              const bookingStatus = String(booking?.status || '').toLowerCase();
              const isCheckedIn = bookingStatus === 'checked_in';
              const isCheckedOut = bookingStatus === 'checked_out';
              const isPartialCheckoutStatus = bookingStatus === 'partial_checkout' || bookingStatus === 'partial checkout';
              const isPartialCheckInStatus =
                bookingStatus === 'partial_checkin' || bookingStatus === 'partial check-in';
              const isCancelledStatus =
                bookingStatus === 'cancelled' ||
                bookingStatus === 'canceled' ||
                bookingStatus === 'no_show' ||
                bookingStatus === 'no show';
              // Reserved must confirm via Modify Booking before check-in.
              // Only fully in-house (no pending rooms) uses Modify; otherwise continue check-in.
              const isModifyFlowStatus =
                bookingStatus === 'reserved'
                || (isCheckedIn && !isContinueCheckInFlow)
                || isCheckedOut
                || isCancelledStatus;
                const isDisabled = (() => {
              // For statuses that should open edit flow, show Modify booking (enabled when permitted)
              if (isModifyFlowStatus) {
                return submitting || !canEditBooking || !onModifyBooking;
              }
              // For fully checked in bookings, button is always enabled (for updates)
              if (!canCheckInByDate) return true;
              // For confirmed and partial check-in bookings, require room selection
              if (submitting || !hasSelectedRooms) return true;
              if (selectedHeldRoom) return true;
              if (hasUncleanSelectedRooms || housekeepingCleaningId) return true;
              if (bookingStatus === 'confirmed' && hasSelectedRooms && !purposeOfStayValid) {
                return true;
              }
              return false;
                })();
                
                const disabledReason = (() => {
                  if (!isDisabled || submitting) return '';
                  if (isModifyFlowStatus && (!canEditBooking || !onModifyBooking)) {
                    return getPermissionDeniedMessage(PERMISSIONS.BOOKING_MODIFY, 'Modify Booking');
                  }
                  if (isModifyFlowStatus) return '';
                  if (!canCheckInByDate) {
                    const checkInLabel = booking?.check_in_date ? moment(booking.check_in_date).format('DD MMM YYYY') : 'the check-in date';
                    return `Check-in is allowed only on or after ${checkInLabel}, based on the hotel's local date/timezone.`;
                  }
                  if (!hasSelectedRooms) {
                    if (isPartialCheckoutStatus) {
                      return 'Select at least one room using the checkbox before check-in';
                    }
                    if (!isPartialCheckInStatus) {
                      return 'Please select at least one room to check in';
                    }
                    return 'Please select at least one additional room to complete check-in';
                  }
                  if (selectedHeldRoom) {
                    return getCheckInRoomHoldTooltip(
                      getCheckInRoomHoldReason(selectedHeldRoom),
                      selectedHeldRoom.room_number,
                    );
                  }
                  if (hasUncleanSelectedRooms || housekeepingCleaningId) {
                    return 'Mark all selected rooms as clean before check-in';
                  }
                  if (bookingStatus === 'confirmed' && hasSelectedRooms && !purposeOfStayValid) {
                    if (purposeOfStay === 'other' && !(purposeOfStayNote || '').trim()) {
                      return 'Please describe the purpose when "Other" is selected';
                    }
                    return 'Please select purpose of stay';
                  }
                  return '';
                })();
                
                const button = (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // For statuses that should open booking edit mode, route to modify callback
                      if (isModifyFlowStatus) {
                        if (!canEditBooking || !onModifyBooking) return;
                        const safeBookingId = Number(bookingId);
                        if (!Number.isInteger(safeBookingId) || safeBookingId <= 0) {
                          setError('Invalid booking reference. Please refresh and try again.');
                          return;
                        }
                        onModifyBooking(safeBookingId);
                      } else if (!canCheckInByDate) {
                        return;
                      } else {
                        // For check-in operations, call handleCheckIn - it will handle validation and show errors
                        handleCheckIn();
                      }
                    }}
                    disabled={isDisabled}
            sx={{
              backgroundColor: CUSTOM_BORDER_COLOR,
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: CUSTOM_BORDER_COLOR,
                opacity: 0.9,
                boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
              },
              '&:disabled': {
                backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                color: isDark ? drawerTheme.subtleText : '#666',
              },
            }}
          >
            {(() => {
              if (submitting) {
                return <CircularProgress size={20} />;
              }
              
              // For statuses handled via edit flow, show "Modify booking"
              if (isModifyFlowStatus) {
                return 'Modify booking';
              }
              
              // Partial / continue check-in / confirmed
              if (
                isPartialCheckInStatus
                || isPartialCheckoutStatus
                || isContinueCheckInFlow
              ) {
                return isPartialRoomSelection ? 'Partial Check-In' : 'Check In';
              }
              
              // For confirmed bookings
              return isPartialRoomSelection ? 'Partial Check-In' : 'Check In';
            })()}
          </Button>
            );
            
                const tooltipTitle = bookingStatus === 'reserved'
                  ? (disabledReason || RESERVED_CONFIRM_TOOLTIP)
                  : disabledReason;
                return tooltipTitle ? (
                  <Tooltip title={tooltipTitle} arrow>
                    <span style={{ width: '100%', display: 'block' }}>{button}</span>
                  </Tooltip>
                ) : button;
              })()}
            </Grid>
          </Grid>
        </Box>

        {/* Error Popup - Inside drawer */}
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
              zIndex: 1400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={() => setError(null)}
          >
            <Paper
              onClick={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                borderRadius: 2,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.2)',
                minWidth: 400,
                maxWidth: 500,
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? drawerTheme.text : '#333',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Error
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: isDark ? drawerTheme.subtleText : '#666',
                  mb: 3,
                }}
              >
                {error}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => setError(null)}
                  variant="contained"
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                    },
                  }}
                >
                  OK
                </Button>
              </Box>
            </Paper>
          </Box>
        )}

      </Box>
    </Drawer>

    {/* Individual Guest Drawer – replaced by BookingGuestFormDrawer */}
    <BookingGuestFormDrawer
      open={individualGuestDrawerOpen}
      onClose={() => {
        setIndividualGuestDrawerOpen(false);
        setEditingGuestItemId(null);
        setEditingGuestId(null);
        setEditingRoomId(null);
        editingRoomIdRef.current = null;
        setDeferGuestPersist(false);
        setInitialGuestFormData(null);
      }}
      bookingId={bookingId}
      booking={booking}
      roomLabel={roomLabelForGuest}
      editingGuestId={editingGuestId}
      initialGuest={initialGuestFormData}
      deferGuestPersist={deferGuestPersist}
      onDeferredSave={(staged) => {
        const roomId = editingRoomIdRef.current ?? editingRoomId;
        if (roomId == null) return;
        setPendingRoomGuests((prev) => ({ ...prev, [roomId]: staged }));
      }}
      resolveItemsToUpdate={resolveItemsForGuestUpdate}
      onSaved={async () => {
        setSuccessMessage('Guest information updated successfully.');
        await fetchAvailableRooms({ preserveSelection: true });
      }}
      onSuccessMessage={setSuccessMessage}
    />

      <GuestDocumentsDrawer
        open={documentDrawerOpen}
        onClose={() => setDocumentDrawerOpen(false)}
        documents={selectedGuestDocuments}
        isDark={isDark}
      />

      {/* Delete Guest Confirmation Dialog - Inside Drawer */}
      <Dialog
        open={deleteGuestDialogOpen}
        onClose={() => {
          setDeleteGuestDialogOpen(false);
          setGuestToDelete(null);
        }}
        container={drawerContainerRef.current || undefined}
        sx={{
          position: 'absolute',
          zIndex: 1400,
        }}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
            borderRadius: 3,
            minWidth: 400,
            maxWidth: 500,
            margin: 2,
            position: 'absolute',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            position: 'absolute',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
            pb: 2,
          }}
        >
          <DeleteIcon sx={{ color: isDark ? '#f44336' : '#d32f2f', fontSize: 24 }} />
          <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
            Remove Guest
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ color: isDark ? drawerTheme.text : '#333', mb: 2 }}>
            Are you sure you want to remove <strong>{guestToDelete?.guestName || 'this guest'}</strong> from this room?
          </Typography>
          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
            This action cannot be undone. The guest will be removed from all booking items for this room.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${isDark ? '#444' : '#e0e0e0'}` }}>
          <Button
            onClick={() => {
              setDeleteGuestDialogOpen(false);
              setGuestToDelete(null);
            }}
            variant="outlined"
            disabled={savingGuests}
            sx={{
              borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
              color: isDark ? drawerTheme.text : '#666',
              '&:hover': {
                borderColor: isDark ? '#444' : '#ccc',
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!guestToDelete || !guestToDelete.guest?.id || !guestToDelete.bookingItemId) {
                setError('Invalid guest information.');
                setDeleteGuestDialogOpen(false);
                return;
              }
              
              try {
                setSavingGuests(true);
                setError(null);
                
                // Call API to remove guest
                const response = await api.patch(`/booking/api/${bookingId}/`, {
                  booking_item_id: guestToDelete.bookingItemId,
                  remove_guest_id: guestToDelete.guest.id,
                });
                
                if (response.data.success) {
                  setSuccessMessage('Guest removed successfully.');
                  setDeleteGuestDialogOpen(false);
                  setGuestToDelete(null);
                  // Refresh booking data but keep room selection
                  await fetchAvailableRooms({ preserveSelection: true });
                } else {
                  setError(response.data.error || 'Failed to remove guest.');
                }
              } catch (err) {
                const errorData = err.response?.data || {};
                const errorMsg = errorData.error || errorData.message || err.message || 'Failed to remove guest.';
                setError(errorMsg);
              } finally {
                setSavingGuests(false);
              }
            }}
            variant="contained"
            disabled={savingGuests}
            sx={{
              backgroundColor: isDark ? '#f44336' : '#d32f2f',
              color: '#fff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: isDark ? '#d32f2f' : '#b71c1c',
              },
              '&:disabled': {
                backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            {savingGuests ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Remove Guest'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Drawer */}
      <Drawer
        anchor="right"
        open={paymentDrawerOpen}
        onClose={() => {
          setPaymentDrawerOpen(false);
          setPaymentAmount(0);
          setPaymentDate(moment());
          setPaymentMethod('');
          setPaymentLinkSendOpen(false);
          setReferenceNumber('');
          setPaymentReceipt(null);
          setPaymentCameraUpload(false);
        }}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
          },
        }}
      >
        <Box ref={paymentDrawerRootRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
              }}
            >
              <PaymentDrawerHeaderTitle
                booking={booking}
                bookingId={bookingId}
                paymentHistory={paymentHistory}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accentColor={CUSTOM_BORDER_COLOR}
              />
              <IconButton onClick={() => {
                setPaymentDrawerOpen(false);
                setPaymentAmount(0);
                setPaymentDate(moment());
                setPaymentMethod('');
                setPaymentLinkSendOpen(false);
                setReferenceNumber('');
                setPaymentReceipt(null);
                setPaymentCameraUpload(false);
              }} sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            {/* Progress Bar */}
            {submittingPayment && (
              <LinearProgress
                sx={{
                  height: 3,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: CUSTOM_BORDER_COLOR,
                  },
                }}
              />
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Payment Summary */}
            {booking && (
              <BookingPaymentSummaryCard
                summary={getPaymentSummaryData(booking, paymentHistory)}
                booking={booking}
                bookingId={bookingId}
                isDark={isDark}
                drawerTheme={drawerTheme}
                overlayMountRef={paymentDrawerRootRef}
                onExtraChargesSaved={async (result) => {
                  if (result?.booking) {
                    setBooking(result.booking);
                  } else if (bookingId) {
                    try {
                      const data = await getBookingDetails(bookingId);
                      setBooking(data);
                    } catch {
                      // ignore refresh errors
                    }
                  }
                }}
              />
            )}

            {/* Payment History */}
            {(loadingPayments) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, mb: 2 }}>
                <CircularProgress size={24} sx={{ color: CUSTOM_BORDER_COLOR }} />
              </Box>
            ) : (
              <BookingFinancialHistoryPanel
                payments={Array.isArray(paymentHistory) ? paymentHistory : []}
                currency={booking?.currency || 'INR'}
                hotelTimezone={booking?.hotel_timezone}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accent={CUSTOM_BORDER_COLOR}
                defaultExpanded={false}
              />
            )}

            {/* Collect Payment Form */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                borderRadius: '12px',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600, mb: 1.5 }}>
                Collect Payment
              </Typography>

              <Grid container spacing={1.5}>
                {/* Payment Amount */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentAmount != null && paymentAmount !== '' ? round2(Number(paymentAmount)) : ''}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setPaymentAmount(Number.isFinite(value) && value >= 0 ? round2(value) : 0);
                    }}
                    inputProps={{
                      step: "0.01",
                      min: "0",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {bookingCurrencySymbol}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label="Payment Date & Time"
                      disableFuture
                      value={paymentDate}
                      onChange={setPaymentDate}
                      slotProps={{
                        ...datePickerSlotProps,
                        textField: {
                          fullWidth: true,
                          size: 'medium',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                              '& fieldset': {
                                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Payment Method - Chips */}
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', fontWeight: 600, mb: 1, display: 'block' }}>
                    Payment Method *
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {paymentMethods.map((method) => (
                      <Chip
                        key={method}
                        label={method}
                        onClick={() => {
                          setPaymentMethod(method);
                          setPaymentLinkSendOpen(method === PAYMENT_LINK_METHOD && canSendPaymentLink);
                        }}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: paymentMethod === method
                            ? CUSTOM_BORDER_COLOR
                            : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                          color: paymentMethod === method
                            ? '#000'
                            : CUSTOM_BORDER_COLOR,
                          border: `1px solid ${paymentMethod === method
                            ? CUSTOM_BORDER_COLOR
                            : (isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)')}`,
                          fontWeight: paymentMethod === method ? 700 : 600,
                          fontSize: '0.75rem',
                          height: 32,
                          '&:hover': {
                            backgroundColor: paymentMethod === method
                              ? CUSTOM_BORDER_COLOR
                              : (isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'),
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>

                {/* Reference Number */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reference Number (Optional)"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Payment Receipt Upload */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                        Payment Receipt (Optional)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                        <FormControlLabel
                          label="Camera upload"
                          labelPlacement="start"
                          control={
                            <Switch
                              checked={paymentCameraUpload}
                              onChange={(e) => setPaymentCameraUpload(e.target.checked)}
                              color="primary"
                            />
                          }
                          sx={{ color: isDark ? drawerTheme.text : '#333' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Paper
                    sx={{
                      p: 2,
                      border: `2px dashed ${CUSTOM_BORDER_COLOR}`,
                      borderRadius: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      minHeight: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                    onClick={handlePaymentReceiptClick}
                  >
                    {paymentReceipt ? (
                      <Box>
                        <img
                          src={paymentReceipt}
                          alt="Payment Receipt"
                          style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                        />
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPaymentReceipt(null);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        {paymentCameraUpload ? (
                          <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                        ) : (
                          <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                        )}
                        <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                          {paymentCameraUpload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                        </Typography>
                      </Box>
                    )}
                    <input
                      ref={paymentReceiptFileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handlePaymentReceiptUpload}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setPaymentDrawerOpen(false);
                    setPaymentAmount(0);
                    setPaymentDate(moment());
                    setPaymentMethod('');
                    setPaymentLinkSendOpen(false);
                    setReferenceNumber('');
                    setPaymentReceipt(null);
                    setPaymentCameraUpload(false);
                  }}
                  disabled={submittingPayment}
                  sx={{
                    borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.text : '#666',
                    '&:hover': {
                      borderColor: CUSTOM_BORDER_COLOR,
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: CUSTOM_BORDER_COLOR,
                    },
                    '&:disabled': {
                      borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                      color: isDark ? drawerTheme.subtleText : '#999',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmitPayment}
                  disabled={submittingPayment || !paymentAmount || !paymentMethod || paymentMethod === 'PAYMENT LINK'}
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                      boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    },
                    '&:disabled': {
                      backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                      color: isDark ? drawerTheme.subtleText : '#666',
                    },
                  }}
                >
                  {submittingPayment ? <CircularProgress size={20} /> : 'Collect Payment'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {canSendPaymentLink ? (
          <PaymentLinkDrawerOverlay
            open={paymentLinkSendOpen}
            onClose={() => setPaymentLinkSendOpen(false)}
            bookingId={bookingId}
            booking={booking}
            amount={paymentAmount}
            currency={booking?.currency || 'INR'}
            isDark={isDark}
            accent={CUSTOM_BORDER_COLOR}
            canSendEmail={canSendPaymentLinkEmail}
            canSendWhatsApp={canSendPaymentLinkWhatsApp}
            onSentSuccess={(msg) => setSuccessMessage(msg)}
            onAfterSend={async () => {
              await fetchPaymentHistory(booking?.code || booking?.booking_code);
              await fetchAvailableRooms();
            }}
          />
          ) : null}
        </Box>

        {/* Payment Receipt Camera Modal */}
        {paymentCameraOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              zIndex: 1500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                stopPaymentCamera();
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                Capture Payment Receipt
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <video
                  ref={paymentReceiptVideoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
                <canvas ref={paymentReceiptCanvasRef} style={{ display: 'none' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={capturePaymentReceipt}
                  sx={{
                    backgroundColor: CUSTOM_BORDER_COLOR,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      opacity: 0.9,
                    },
                  }}
                >
                  Capture
                </Button>
                <Button
                  variant="outlined"
                  onClick={stopPaymentCamera}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>

      <DashboardHousekeepingDrawer
        cleaningId={housekeepingCleaningId}
        open={Boolean(housekeepingCleaningId)}
        onClose={handleHousekeepingClose}
        onSuccess={handleHousekeepingSuccess}
        onError={handleHousekeepingError}
        themeMode={currentTheme}
      />

      {canViewBookingAudit && (
        <BookingAuditDrawer
          open={auditDrawerOpen}
          onClose={() => setAuditDrawerOpen(false)}
          bookingId={auditBookingId}
          bookingCode={booking?.code || booking?.booking_code}
          isDark={isDark}
        />
      )}

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{
            backgroundColor: CUSTOM_BORDER_COLOR,
            color: '#000',
            fontWeight: 600,
            '& .MuiAlert-icon': {
              color: '#000',
            },
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(housekeepingError)}
        autoHideDuration={6000}
        onClose={() => setHousekeepingError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setHousekeepingError('')}
          severity="error"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {housekeepingError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={cameraPermissionHint.open}
        autoHideDuration={9000}
        onClose={() => setCameraPermissionHint((h) => ({ ...h, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCameraPermissionHint((h) => ({ ...h, open: false }))}
          severity="warning"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {cameraPermissionHint.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CheckInDrawer;
